/* =========================================================
   DESIGN INSPIRATION CENTER
   UNISOURCE APPAREL
========================================================= */

(function () {
  "use strict";

  /*
     v62:
     Do NOT block duplicate script instances here.
     If an older script loads first, a singleton guard can prevent the latest
     pagination fix from running. The capture-phase pagination handler below
     is designed to intercept pager clicks before stale bubbling handlers.
  */
  window.__UNISOURCE_DI_ACTIVE_VERSION__ = "v123-customize-cta-rating-toggle-preview";
  console.info("[Design Inspiration] Loaded v123-customize-cta-rating-toggle-preview");

  const CONFIG = window.UNISOURCE_DESIGN_INSPIRATION || {};

  const DISPLAY_SETTINGS_URL = "/apps/inksoft/storefront/display-settings";

  const DEFAULT_DISPLAY_SETTINGS = {
    designIdeas: {
      showReferenceLine: false,
      showReferenceInArtworkName: false,
      showTags: false,
      showRatings: false,
    },
  };

  let storefrontDisplaySettings = DEFAULT_DISPLAY_SETTINGS;

  async function loadStorefrontDisplaySettings() {
    try {
      const response = await fetch(DISPLAY_SETTINGS_URL, {
        method: "GET",
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      storefrontDisplaySettings =
        payload && payload.data
          ? payload.data
          : DEFAULT_DISPLAY_SETTINGS;
    } catch (error) {
      storefrontDisplaySettings = DEFAULT_DISPLAY_SETTINGS;
      console.warn(
        "[Design Inspiration] Unable to load storefront display settings.",
        error
      );
    }
  }

  function designIdeaDisplaySetting(name) {
    return Boolean(
      storefrontDisplaySettings &&
      storefrontDisplaySettings.designIdeas &&
      storefrontDisplaySettings.designIdeas[name] === true
    );
  }

  const API_BASE =
    CONFIG.apiBase ||
    "/apps/inksoft/design-inspiration";

  const API_FALLBACK_BASE =
    CONFIG.fallbackApiBase ||
    "https://shopify-inksoft-poc-node.azurewebsites.net/api/poc/design-inspiration";

  function withCacheBust(url) {
    try {
      const parsed = new URL(url, window.location.href);
      parsed.searchParams.set("_di_v", String(Date.now()));
      return parsed.toString();
    } catch (error) {
      const joiner = String(url).includes("?") ? "&" : "?";
      return `${url}${joiner}_di_v=${Date.now()}`;
    }
  }

  /*
     Matrix JSON exported from the Excel placement/apparel workbook.

     How to use:
     1. Upload design-inspiration-matrix.json to Shopify Files or Azure Blob.
     2. Paste the public JSON URL below, or set it from the page before this script loads:

        window.UNISOURCE_DESIGN_INSPIRATION = {
          matrixUrl: "https://your-json-url/design-inspiration-matrix.json"
        };

     If this stays blank, the page will continue using the built-in apparel/design behavior.
  */
  function addMatrixCacheBust(url) {
    if (!url) return "";

    try {
      const parsed = new URL(url, window.location.href);
      parsed.searchParams.set("_di_matrix_v", String(Date.now()));
      return parsed.toString();
    } catch (error) {
      const joiner = String(url).includes("?") ? "&" : "?";
      return `${url}${joiner}_di_matrix_v=${Date.now()}`;
    }
  }

  function getDefaultMatrixAssetUrl() {
    const script =
      document.currentScript ||
      Array.from(document.scripts).find((item) =>
        String(item.src || "").includes("design-inspiration.js")
      );

    if (!script || !script.src) return "";

    try {
      const url = new URL(script.src);
      url.pathname = url.pathname.replace(/design-inspiration\.js$/i, "design-inspiration-matrix.json");
      return addMatrixCacheBust(url.toString());
    } catch (error) {
      return "";
    }
  }

  const DI_MATRIX_URL = addMatrixCacheBust(
    CONFIG.matrixUrl ||
      CONFIG.designMatrixUrl ||
      getDefaultMatrixAssetUrl()
  );

  /*
     v40 matrix support - JSON-only source, no embedded matrix fallback.

     v35 matrix support.
     This automatically reads design-inspiration-matrix.json from the same
     Shopify theme Assets folder as design-inspiration.js.

     If a page-level CONFIG.matrixUrl is provided, that external URL still takes priority.
     If the JSON cannot be loaded, the embedded fallback matrix will still be used.
  */
  /*
     v40: Matrix JSON is now the single source of truth.
     The embedded fallback matrix has been removed to avoid stale positioning conflicts.
  */
  const EMBEDDED_DESIGN_MATRIX = null;

  const PAGE_SIZE = Math.max(1, Number(CONFIG.pageSize || 12));
  const FEATURED_LIMIT = Math.max(1, Number(CONFIG.featuredLimit || 16));

  /*
     v84:
     Hardcoded apparel fallback disabled.

     Public apparel must now come from:
     1. Admin-saved design apparel records / mockups / apparelReferences
     2. Matrix curatedApparel records only when intentionally supplied

     This prevents old built-in products such as Gildan 2000 from reappearing
     after they have been removed from the Admin Approved Apparel Library.
  */
  const CURATED_APPAREL_LIBRARY = [];

  const COLOR_FAMILY_ORDER = ["White", "Black", "Grey", "Royal", "Navy"];

  const DEFAULT_APPAREL =
    CURATED_APPAREL_LIBRARY.find((item) =>
      String(item.productId) === "1010290" &&
      String(item.styleId) === "1106313"
    ) ||
    CURATED_APPAREL_LIBRARY.find((item) =>
      item.category === "Short Sleeve T-Shirt" &&
      item.colorFamily === "White"
    ) ||
    CURATED_APPAREL_LIBRARY.find((item) => item.front) ||
    null;

  const els = {
    loader: document.getElementById("design-inspiration-loader"),
    status: document.getElementById("di-global-status"),
    heroHeadline: document.getElementById("di-hero-headline"),
    heroSubheadline: document.getElementById("di-hero-subheadline"),
    primaryCategories: document.getElementById("di-primary-categories"),
    categoryPrev: document.getElementById("di-category-scroll-prev"),
    categoryNext: document.getElementById("di-category-scroll-next"),
    categoryControls: document.getElementById("di-category-scroll-controls"),
    featuredSection: document.getElementById("di-featured-section"),
    featuredScroller: document.getElementById("di-featured-scroller"),
    featuredPrev: document.getElementById("di-featured-prev"),
    featuredNext: document.getElementById("di-featured-next"),
    allDesigns: document.getElementById("di-all-designs"),
    allDesignsPagination: document.getElementById("di-all-designs-pagination"),
    howItWorks: document.getElementById("di-how-it-works"),
    modal: document.getElementById("di-modal"),
    modalContent: document.getElementById("di-modal-content"),
    modalClose: document.getElementById("di-modal-close"),
  };



  function ensureCustomizableBadgeStyles() {
    if (document.getElementById("di-customizable-badge-styles")) return;
    const style = document.createElement("style");
    style.id = "di-customizable-badge-styles";
    style.textContent = `
      .di-customizable-badge{display:inline-flex!important;align-items:center!important;gap:6px!important;border-radius:999px!important;padding:6px 10px!important;background:#fff3df!important;color:#9a4d00!important;border:1px solid #f3c98b!important;font-size:11px!important;font-weight:900!important;letter-spacing:.02em!important;line-height:1!important;box-shadow:0 2px 8px rgba(15,23,42,.16)!important;visibility:visible!important;opacity:1!important}
      .di-design-card__image-wrap{position:relative!important;isolation:isolate!important}
      .di-design-card__image-wrap>.di-product-mockup-grid{position:relative!important;z-index:1!important}
      .di-design-card__customizable-badge{position:absolute!important;top:12px!important;left:12px!important;right:auto!important;bottom:auto!important;z-index:2147483647!important;max-width:calc(100% - 24px)!important;white-space:nowrap!important;pointer-events:none!important;transform:none!important}
      .di-visualizer__customizable-badge{margin:0 0 12px!important}
    `;
    document.head.appendChild(style);
  }

  function isTruthyCustomizableFlag(value) {
    if (value === true || value === 1) return true;

    const normalized = String(value == null ? "" : value)
      .trim()
      .toLowerCase();

    return ["true", "1", "yes", "y", "on", "enabled"].includes(normalized);
  }

  function customizationBadgeLabel(design) {
    if (!design) return "";

    /*
      The storefront badge is controlled only by the Admin UI checkbox.
      A design title, tag, or customization type must never enable it by itself.
    */
    const explicitFlag = isTruthyCustomizableFlag(
      design.isCustomizable ??
      design.customizable ??
      design.showCustomizableBadge ??
      design.showEditableBadge
    );

    if (!explicitFlag) return "";

    const type = String(
      design.customizationType ||
      design.customizableType ||
      "text-and-color"
    ).trim().toLowerCase();

    if (["text", "text-only"].includes(type)) return "Customize Text";
    if (["color", "colors", "color-only"].includes(type)) return "Customize Colors";
    if (["full", "fully-customizable", "fully customizable"].includes(type)) return "Fully Customizable";
    return "Customize Text + Colors";
  }

  function isDesignCustomizable(design) {
    return Boolean(customizationBadgeLabel(design));
  }

  function customizableReference(design) {
    if (!isDesignCustomizable(design)) return "";

    const id = String(
      design?.inksoftDesignId ||
      design?.designId ||
      design?.id ||
      ""
    ).trim();

    return id ? `CUST-${id.replace(/^CUST-/i, "")}` : "";
  }

  function stripCustomizableReference(value) {
    return String(value || "")
      .replace(/\s*[—–-]\s*CUST-\d+\s*$/i, "")
      .replace(/\s*\(CUST-\d+\)\s*$/i, "")
      .trim();
  }

  function titleWithCustomizableReference(title, design) {
    const cleanTitle = stripCustomizableReference(title);

    if (!designIdeaDisplaySetting("showReferenceInArtworkName")) {
      return cleanTitle;
    }

    const reference = customizableReference(design);
    return reference ? `${cleanTitle} — ${reference}` : cleanTitle;
  }

  function getDesignSimpleCategories(design) {
    const categories = [];
    const add = (value) => {
      const normalized = String(value || "").trim().toLowerCase();
      if (normalized && !categories.includes(normalized)) categories.push(normalized);
    };

    if (isDesignCustomizable(design)) add("customizable");

    const source = normalizeFilterText([
      design?.audience,
      design?.segment,
      design?.collection,
      design?.activity,
      design?.schoolName,
      design?.school,
      design?.organizationName,
      design?.organization,
      ...(Array.isArray(design?.tags) ? design.tags : []),
      ...(Array.isArray(design?.keywords) ? design.keywords : [])
    ].filter(Boolean).join(" "));

    if (
      source.includes("school") ||
      source.includes("student") ||
      source.includes("teacher") ||
      source.includes("pe uniform") ||
      source.includes("spirit wear") ||
      source.includes("graduation")
    ) add("school");

    if (
      source.includes("business") ||
      source.includes("company") ||
      source.includes("corporate") ||
      source.includes("employee") ||
      source.includes("staff uniform")
    ) add("business");

    if (
      source.includes("organization") ||
      source.includes("organisation") ||
      source.includes("team") ||
      source.includes("group") ||
      source.includes("club") ||
      source.includes("association") ||
      source.includes("league")
    ) add("organization");

    const schoolName = String(design?.schoolName || design?.school || "").trim();
    const organizationName = String(design?.organizationName || design?.organization || "").trim();

    if (schoolName) add("school");
    if (organizationName && !schoolName && !categories.includes("business")) add("organization");

    return categories;
  }

  ensureCustomizableBadgeStyles();

  const designSearchState = {
    allDesigns: [],
    filteredDesigns: [],
    initialized: false,
    filters: {
      search: "",
      categories: []
    }
  };

  function showLoader() {
    if (els.loader) els.loader.style.display = "flex";
  }

  function hideLoader() {
    if (els.loader) els.loader.style.display = "none";
  }

  function setStatus(message, type = "info") {
    if (!els.status) return;
    els.status.textContent = message;
    els.status.className = `di-status di-status--${type}`;
  }

  async function fetchJson(url) {
    const response = await fetch(withCacheBust(url), {
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}: ${url}`);
    return response.json();
  }

  async function fetchDesignApi(path) {
    const cleanPath = String(path || "").replace(/^\/+/, "");
    const bases = Array.from(
      new Set([
        API_BASE,
        API_FALLBACK_BASE,
      ].filter(Boolean))
    );

    let lastError = null;

    for (const base of bases) {
      const cleanBase = String(base || "").replace(/\/+$/, "");
      const url = `${cleanBase}/${cleanPath}`;

      try {
        const payload = await fetchJson(url);
        console.log("[Design Inspiration Center] API loaded:", url, payload);
        return payload;
      } catch (error) {
        lastError = error;
        console.warn("[Design Inspiration Center] API failed:", url, error);
      }
    }

    throw lastError || new Error(`Unable to load Design Inspiration API path: ${path}`);
  }

  let DESIGN_MATRIX = null;
  let DESIGN_MATRIX_BY_ID = new Map();
  let DESIGN_MATRIX_BY_TITLE = new Map();
  let MATRIX_APPAREL_LIBRARY = [];

  function normalizeMatrixLookupText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/&amp;/g, "&")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function getDesignLookupTitles(design) {
    if (!design) return [];

    return [
      design.title,
      design.name,
      design.publicTitle,
      design.public_title,
      design.artworkTitle,
      design.artwork_title,
      design.slug,
    ]
      .filter(Boolean)
      .map(normalizeMatrixLookupText)
      .filter(Boolean);
  }

  function getMatrixLookupTitles(artwork) {
    if (!artwork) return [];

    return [
      artwork.publicTitle,
      artwork.public_title,
      artwork.artworkTitle,
      artwork.artwork_title,
      artwork.title,
      artwork.name,
      artwork.slug,
    ]
      .filter(Boolean)
      .map(normalizeMatrixLookupText)
      .filter(Boolean);
  }

  function normalizeMatrixApparelRecord(record) {
    if (!record) return null;

    const mockups = record.mockups || {};
    const productId = String(record.productId || "");
    const styleId = String(record.styleId || "");
    const colorName = String(record.colorName || "");

    if (!productId || !styleId) return null;

    return {
      key:
        record.key ||
        [
          record.brand || "Apparel",
          record.manufacturerSku || record.productSku || productId,
          colorName || styleId,
        ]
          .filter(Boolean)
          .join("-"),
      category: record.apparelType || record.category || "Apparel",
      type: record.apparelType || record.type || "Apparel",
      brand: record.brand || "",
      mfgSku: record.manufacturerSku || record.mfgSku || "",
      productSku: record.productSku || "",
      productId,
      styleId,
      defaultStyle: Boolean(record.defaultForType || record.defaultStyle),
      colorFamily: record.curatedColorFamily || record.colorFamily || colorName,
      colorName,
      tone: record.garmentTone || record.tone || "",
      productName: record.productName || "",
      front: mockups.Front || record.front || "",
      back: mockups.Back || record.back || "",
      left: mockups.Left || record.left || "",
      right: mockups.Right || record.right || "",
      views: ["Front", "Back", "Left", "Right"]
        .filter((side) => mockups[side] || record[String(side).toLowerCase()])
        .join(", "),
      designOnlineUrl: record.designOnlineUrl || "",
      sizes: Array.isArray(record.availableSizes)
        ? record.availableSizes.join(", ")
        : record.sizes || "",
      sideMapping: record.sideMapping || null,
    };
  }


  function asPlainObject(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  }

  function firstAdminValue(...values) {
    for (const value of values) {
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        return value;
      }
    }
    return "";
  }

  function cleanAdminImageUrl(value) {
    const url = String(value || "").trim();
    if (!url) return "";

    /* Ignore placeholder/example values accidentally pasted while testing. */
    if (url.includes("...")) return "";

    /*
      v81:
      Do not trust auto-generated InkSoft product/style image paths like:
      https://cdn.inksoft.com/images/products/1010496/styles/1109225/front/500.png
      Those URLs 404 in the browser. When we detect that pattern, return blank
      so the renderer will fall back to the approved apparel library's real
      publisher/product image URL.
    */
    if (/cdn\.inksoft\.com\/images\/products\/\d+\/styles\/\d+\//i.test(url)) return "";
    if (/\/images\/products\/\d+\/styles\/\d+\//i.test(url)) return "";

    if (/^https?:\/\//i.test(url)) return url;
    if (url.indexOf("//") === 0) return `https:${url}`;

    return url;
  }


  function isUnsafeRenderedMockupUrl(url) {
    const value = String(url || "").trim().toLowerCase();
    if (!value) return false;

    /*
      v98:
      Reject image URLs that are likely design-render outputs instead of blank
      garment/product mockup images. These old rendered URLs can contain baked-in
      artwork and keep appearing after the Admin apparel product/color is changed.
    */
    return (
      value.includes("/jitimage/") ||
      value.includes("/images/publishers/") ||
      value.includes("/publishers/") ||
      value.includes("/designs/") ||
      value.includes("/rendered/") ||
      value.includes("/mockups/") ||
      value.includes("designid=") ||
      value.includes("inksoftdesignid=") ||
      value.includes("transparentartworkurl=") ||
      value.includes("designartworkurl=")
    );
  }

  function safeGarmentMockupUrl(value) {
    const cleaned = cleanAdminImageUrl(value);
    if (!cleaned) return "";
    if (isUnsafeRenderedMockupUrl(cleaned)) return "";
    return cleaned;
  }

  function preferOriginalMockupUrl(url) {
    const text = normalizeImage(url);
    if (!text) return "";
    return text.replace(/\/500\.png(\?[^\"'\s]*)?$/i, "/Original.png$1");
  }

  function mockupUrlCandidates() {
    const seen = new Set();
    const output = [];
    Array.prototype.slice.call(arguments).forEach((value) => {
      const clean = normalizeImage(value);
      if (!clean) return;
      [preferOriginalMockupUrl(clean), clean].forEach((url) => {
        if (url && !seen.has(url)) {
          seen.add(url);
          output.push(url);
        }
      });
    });
    return output;
  }

  function apparelRecordKey(item) {
    return [
      String(item && item.productId || "").trim(),
      String(item && item.styleId || "").trim(),
      String(item && item.colorName || "").trim().toLowerCase(),
    ].join("|");
  }

  function uniqueApparelRecords(records) {
    const seen = new Set();
    return (records || []).filter((item) => {
      if (!item) return false;
      const key = apparelRecordKey(item) || String(item.key || "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function getAdminMockupSource(design) {
    if (!design || typeof design !== "object") return null;

    const candidates = [];

    /*
      v80:
      The public /search endpoint can contain both:
      - legacy/top-level product fields from older matrix/default data
      - Admin-saved mockup/product references inside mockup/defaultMockup/
        primaryMockup/productReference/productStyleReference/apparelReference

      The modal must prefer the Admin-saved nested reference first. Otherwise
      the final render can fall back to older curated references such as
      Gildan 2000 White even when Admin has saved another product/style/color.

      Priority order:
      1. Explicit Admin/product reference objects
      2. Mockup arrays/items
      3. Top-level design object as last fallback only
    */

    [
      design.mockup,
      design.defaultMockup,
      design.primaryMockup,
      design.productStyleReference,
      design.productReference,
      design.apparelReference,
      design.defaultApparel,
      design.apparel,
    ].forEach((item) => {
      if (item) candidates.push(item);
    });

    if (Array.isArray(design.mockups)) {
      candidates.push(...design.mockups);
    } else if (design.mockups && typeof design.mockups === "object") {
      if (design.mockups.default) candidates.push(design.mockups.default);
      if (design.mockups.primary) candidates.push(design.mockups.primary);
      if (Array.isArray(design.mockups.items)) candidates.push(...design.mockups.items);
      if (Array.isArray(design.mockups.records)) candidates.push(...design.mockups.records);
      candidates.push(design.mockups);
    }

    if (Array.isArray(design.productReferences)) {
      candidates.push(...design.productReferences);
    }

    if (Array.isArray(design.apparelReferences)) {
      candidates.push(...design.apparelReferences);
    }

    /*
      Top-level design fields are useful for older API payloads, but they must
      be last because they may contain stale matrix/default product values.
    */
    candidates.push(design);

    return (
      candidates.find((item) => {
        const record = asPlainObject(item);
        return firstAdminValue(
          record.productId,
          record.inksoftProductId,
          record.product_id,
          record.styleId,
          record.inksoftStyleId,
          record.productStyleId,
          record.style_id,
          record.productName,
          record.frontImageUrl,
          record.frontGarmentImageUrl,
          record.front,
          record.colorName,
          record.defaultColor,
          record.defaultApparelType,
          record.apparelType
        );
      }) || null
    );
  }

  function getAdminEnabledSidesForDesign(design) {
    const found = [];

    const addSide = (value) => {
      const side = normalizeSideName(value);
      if (side && !found.includes(side)) found.push(side);
    };

    const addEnabled = (enabled) => {
      if (!enabled) return;

      if (Array.isArray(enabled)) {
        enabled.forEach(addSide);
        return;
      }

      if (typeof enabled === "string") {
        enabled.split(/[;,|]/g).forEach(addSide);
        return;
      }

      if (enabled && typeof enabled === "object") {
        ["Front", "Back", "Left", "Right"].forEach((side) => {
          const key = side.toLowerCase();

          if (
            enabled[side] === true ||
            enabled[key] === true ||
            enabled[`${key}Enabled`] === true ||
            enabled[`show${side}`] === true ||
            enabled[`enable${side}`] === true
          ) {
            addSide(side);
          }
        });
      }
    };

    /*
      IMPORTANT:
      Admin panel checkbox state is the source of truth.

      Do NOT auto-add Left/Right just because Front/Back exist.
      Some artwork has 4-side artwork available in InkSoft, but the public
      visualizer should only show the sides enabled in Admin.
    */

    addEnabled(design && design.enabledViews);
    addEnabled(design && design.viewsEnabled);
    addEnabled(design && design.artworkViewsEnabled);
    addEnabled(design && design.views);
    addEnabled(design && design.sides);

    const rowSources = [];

    if (Array.isArray(design && design.mockups)) rowSources.push(...design.mockups);
    if (Array.isArray(design && design.apparelReferences)) rowSources.push(...design.apparelReferences);
    if (Array.isArray(design && design.productReferences)) rowSources.push(...design.productReferences);
    if (Array.isArray(design && design.artworkViews)) rowSources.push(...design.artworkViews);

    if (design && design.mockup) rowSources.push(design.mockup);
    if (design && design.defaultMockup) rowSources.push(design.defaultMockup);
    if (design && design.primaryMockup) rowSources.push(design.primaryMockup);
    if (design && design.productReference) rowSources.push(design.productReference);
    if (design && design.apparelReference) rowSources.push(design.apparelReference);

    rowSources.forEach((row) => {
      if (!row || typeof row !== "object") return;

      addEnabled(row.enabledViews);
      addEnabled(row.viewsEnabled);
      addEnabled(row.artworkViewsEnabled);
      addEnabled(row.views);
      addEnabled(row.sides);

      /*
        Only use row.side / row.view when it is clearly coming from an
        enabled Admin row. This prevents existing InkSoft artwork side data
        from forcing extra public mockups.
      */
      const rowIsEnabled =
        row.enabled === true ||
        row.isEnabled === true ||
        row.active === true ||
        row.selected === true ||
        row.show === true ||
        row.displaySide === true ||
        row.publicEnabled === true ||
        row.publicVisible === true;

      if (rowIsEnabled && (row.side || row.view || row.placementSide)) {
        addSide(row.side || row.view || row.placementSide);
      }
    });

    /*
      If Admin has no explicit side data, default safely to Front only.
      This avoids accidentally rendering all available InkSoft sides.
    */
    if (!found.length) addSide("Front");

    const order = {
      Front: 1,
      Back: 2,
      Left: 3,
      Right: 4,
    };

    return found.sort((a, b) => (order[a] || 99) - (order[b] || 99));
  }

  function getAdminApparelRecordForDesign(design) {
    /*
      v96:
      Strict Admin apparel resolver.

      This function intentionally prevents old/rendered mockups from returning.

      Rules:
      - Admin product + style + color is the source of truth.
      - Approved library is used only as an exact support match.
      - No product-only fallback.
      - No style-only fallback.
      - No old matrix/default fallback.
      - No side uses front as fallback.
    */

    const source = asPlainObject(getAdminMockupSource(design));
    if (!source || !Object.keys(source).length) return null;

    const productId = String(
      firstAdminValue(
        source.productId,
        source.inksoftProductId,
        source.product_id
      )
    ).trim();

    const styleId = String(
      firstAdminValue(
        source.styleId,
        source.inksoftStyleId,
        source.productStyleId,
        source.style_id
      )
    ).trim();

    const colorName = String(
      firstAdminValue(
        source.colorName,
        source.color,
        source.selectedColor,
        source.defaultColor
      )
    ).trim();

    /*
      Strict requirement:
      Product ID and Style ID must both exist.
      If either one is missing, returning null is safer than guessing and
      accidentally reviving an old Gildan / old color / old rendered image.
    */
    if (!productId || !styleId) return null;

    const library = getCombinedApparelLibrary();

    const normalizeColor = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const wantedColor = normalizeColor(colorName);

    const colorMatches = (item) => {
      if (!wantedColor) return true;

      return [
        item.colorName,
        item.colorFamily,
        item.curatedColorFamily,
        item.defaultColor,
        item.color
      ].some((value) => normalizeColor(value) === wantedColor);
    };

    /*
      v96:
      Exact match only. This blocks:
      - Product-only fallback
      - Style-only fallback
      - Old default 18500 / 5000 / 2000 records
      - Old matrix mockup rows
    */
    const exactLibraryMatch =
      library.find((item) =>
        String(item.productId || "") === productId &&
        String(item.styleId || "") === styleId &&
        colorMatches(item)
      ) || null;

    const fallback = exactLibraryMatch || {};

    const category = String(
      firstAdminValue(
        source.defaultApparelType,
        source.apparelType,
        source.category,
        source.type,
        design.defaultApparelType,
        fallback.category,
        "Apparel"
      )
    ).trim();

    /*
      v98:
      The old issue is not only fallback order. Some Admin/design mockup rows
      still carry design-rendered garment images. Those images can already have
      artwork baked into the shirt, so they must not win over the Approved
      Apparel Library.

      Therefore the side mockup source order is now:
      1. Exact Approved Apparel Library product/style/color side image
      2. Safe row image only when the exact library side is blank
      3. Nothing

      No side borrows from another side. No product-only/style-only fallback.
    */
    const sourceFrontImage = safeGarmentMockupUrl(
      firstAdminValue(
        source.apparelImageUrl,
        source.frontImageUrl,
        source.frontGarmentImageUrl,
        source.frontUrl,
        source.front
      )
    );

    const sourceBackImage = safeGarmentMockupUrl(
      firstAdminValue(
        source.backImageUrl,
        source.backGarmentImageUrl,
        source.backUrl,
        source.back
      )
    );

    const sourceLeftImage = safeGarmentMockupUrl(
      firstAdminValue(
        source.leftImageUrl,
        source.leftGarmentImageUrl,
        source.leftSleeveImageUrl,
        source.leftUrl,
        source.left
      )
    );

    const sourceRightImage = safeGarmentMockupUrl(
      firstAdminValue(
        source.rightImageUrl,
        source.rightGarmentImageUrl,
        source.rightSleeveImageUrl,
        source.rightUrl,
        source.right
      )
    );

    const frontImage = mockupUrlCandidates(
      safeGarmentMockupUrl(fallback.front),
      safeGarmentMockupUrl(fallback.frontImageUrl),
      sourceFrontImage
    )[0] || "";

    const backImage = mockupUrlCandidates(
      safeGarmentMockupUrl(fallback.back),
      safeGarmentMockupUrl(fallback.backImageUrl),
      sourceBackImage
    )[0] || "";

    const leftImage = mockupUrlCandidates(
      safeGarmentMockupUrl(fallback.left),
      safeGarmentMockupUrl(fallback.leftImageUrl),
      sourceLeftImage
    )[0] || "";

    const rightImage = mockupUrlCandidates(
      safeGarmentMockupUrl(fallback.right),
      safeGarmentMockupUrl(fallback.rightImageUrl),
      sourceRightImage
    )[0] || "";

    const normalized = {
      key: [
        "admin",
        productId,
        styleId,
        colorName || fallback.colorName || fallback.colorFamily || ""
      ]
        .filter(Boolean)
        .join("-"),

      category,

      type: String(
        firstAdminValue(
          source.apparelType,
          source.type,
          fallback.type,
          category
        )
      ).trim(),

      brand: String(
        firstAdminValue(
          source.brand,
          source.manufacturer,
          source.manufacturerBrand,
          fallback.brand
        )
      ).trim(),

      mfgSku: String(
        firstAdminValue(
          source.manufacturerSku,
          source.mfgSku,
          source.sku,
          fallback.mfgSku
        )
      ).trim(),

      productSku: String(
        firstAdminValue(
          source.productSku,
          source.sku,
          fallback.productSku
        )
      ).trim(),

      productId,
      styleId,

      defaultStyle: true,

      colorFamily: String(
        firstAdminValue(
          source.colorFamily,
          colorName,
          fallback.colorFamily
        )
      ).trim(),

      colorName: colorName || String(fallback.colorName || ""),
      colorHex: String(firstAdminValue(source.colorHex, source.hex, fallback.colorHex)).trim(),
      tone: String(firstAdminValue(source.tone, source.garmentTone, fallback.tone)).trim(),

      productName: String(
        firstAdminValue(
          source.productName,
          source.name,
          fallback.productName,
          "Selected Apparel"
        )
      ).trim(),

      front: frontImage,
      back: backImage,
      left: leftImage,
      right: rightImage,

      frontImageUrl: frontImage,
      backImageUrl: backImage,
      leftImageUrl: leftImage,
      rightImageUrl: rightImage,

      views: getAdminEnabledSidesForDesign(design).join(", ") || fallback.views || "Front",

      designOnlineUrl:
        source.designOnlineUrl ||
        source.designerUrl ||
        fallback.designOnlineUrl ||
        "",

      sizes: Array.isArray(source.availableSizes)
        ? source.availableSizes.join(", ")
        : source.sizes || fallback.sizes || "",

      adminConfigured: true,
      sideMapping: source.sideMapping || fallback.sideMapping || null,

      placementTemplate: source.placementTemplate || design.placementTemplate || "",
      placementSkuKey: source.placementSkuKey || fallback.placementSkuKey || "",
      placementLabel: source.placementLabel || fallback.placementLabel || "",
      gridPlacement: source.gridPlacement || source.grid || fallback.gridPlacement || fallback.grid || null,
      modalPlacement: source.modalPlacement || source.modal || fallback.modalPlacement || fallback.modal || null,
    };

    return normalized.productId && normalized.styleId ? normalized : null;
  }


  function getAdminApparelRecordsForDesign(design) {
    /*
      v91:
      Aggregate Admin mockup rows by Product ID + Style ID + Color.

      Fixes:
      - Prevents Left/Right/Back mockup rows from becoming the main/front record.
      - Restores correct Front image on design cards.
      - Keeps Back, Left, Right images for the modal.
      - Keeps Admin SKU placement fields.
      - Keeps legacy 18500 / stale fallback blocked.
    */
    if (!design || typeof design !== "object") return [];

    const sources = [];

    const addSource = (item) => {
      if (item && typeof item === "object") sources.push(item);
    };

    if (Array.isArray(design.apparelReferences)) {
      design.apparelReferences.forEach(addSource);
    }

    if (Array.isArray(design.productReferences)) {
      design.productReferences.forEach(addSource);
    }

    if (Array.isArray(design.mockups)) {
      design.mockups.forEach(addSource);
    }

    if (design.mockups && typeof design.mockups === "object" && !Array.isArray(design.mockups)) {
      if (Array.isArray(design.mockups.items)) design.mockups.items.forEach(addSource);
      if (Array.isArray(design.mockups.records)) design.mockups.records.forEach(addSource);
      addSource(design.mockups.default);
      addSource(design.mockups.primary);
    }

    function getRowSide(source) {
      return normalizeSideName(
        source.side ||
        source.view ||
        source.placementSide ||
        source.printSide ||
        source.productSide ||
        source.label ||
        "Front"
      );
    }

    function getRowProductId(source) {
      return String(
        firstAdminValue(
          source.productId,
          source.inksoftProductId,
          source.product_id
        )
      ).trim();
    }

    function getRowStyleId(source) {
      return String(
        firstAdminValue(
          source.styleId,
          source.inksoftStyleId,
          source.productStyleId,
          source.style_id
        )
      ).trim();
    }

    function getRowColorName(source) {
      return String(
        firstAdminValue(
          source.colorName,
          source.color,
          source.selectedColor,
          source.defaultColor
        )
      ).trim();
    }

    function getRowGroupKey(source) {
      return [
        getRowProductId(source),
        getRowStyleId(source),
        getRowColorName(source).toLowerCase()
      ].join("|");
    }

    function rowHasSideSpecificIdentity(source) {
      return Boolean(
        source &&
        (
          source.side ||
          source.view ||
          source.placementSide ||
          source.printSide ||
          source.productSide
        )
      );
    }

    function rowHasPlacement(source) {
      return Boolean(
        source &&
        (
          source.gridPlacement ||
          source.modalPlacement ||
          source.grid ||
          source.modal ||
          source.placementSkuKey
        )
      );
    }

    function rowImageForSide(source, side) {
      if (!source || typeof source !== "object") return "";

      const normalizedSide = normalizeSideName(side);
      const rowSide = getRowSide(source);

      if (normalizedSide === "Front") {
        return cleanAdminImageUrl(
          firstAdminValue(
            source.frontImageUrl,
            source.frontGarmentImageUrl,
            source.frontUrl,
            source.front,
            rowSide === "Front" ? source.apparelImageUrl : ""
          )
        );
      }

      if (normalizedSide === "Back") {
        return cleanAdminImageUrl(
          firstAdminValue(
            source.backImageUrl,
            source.backGarmentImageUrl,
            source.backUrl,
            source.back,
            rowSide === "Back" ? source.apparelImageUrl : ""
          )
        );
      }

      if (normalizedSide === "Left") {
        return cleanAdminImageUrl(
          firstAdminValue(
            source.leftImageUrl,
            source.leftGarmentImageUrl,
            source.leftSleeveImageUrl,
            source.leftUrl,
            source.left,
            rowSide === "Left" ? source.apparelImageUrl : ""
          )
        );
      }

      if (normalizedSide === "Right") {
        return cleanAdminImageUrl(
          firstAdminValue(
            source.rightImageUrl,
            source.rightGarmentImageUrl,
            source.rightSleeveImageUrl,
            source.rightUrl,
            source.right,
            rowSide === "Right" ? source.apparelImageUrl : ""
          )
        );
      }

      return "";
    }

    const grouped = new Map();

    sources.forEach((source) => {
      if (!source || typeof source !== "object") return;

      const productId = getRowProductId(source);
      const styleId = getRowStyleId(source);
      const colorName = getRowColorName(source);

      if (!productId && !styleId) return;

      const groupKey = getRowGroupKey(source);
      if (!groupKey.replace(/\|/g, "").trim()) return;

      const side = getRowSide(source);

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          base: source,
          productId,
          styleId,
          colorName,
          sides: {},
          placementSources: []
        });
      }

      const bucket = grouped.get(groupKey);

      /*
        Keep side rows as side rows.
        Do not allow a Left/Right/Back mockup row to become the main/front apparel base.
      */
      const sourceIsPlainReference = !rowHasSideSpecificIdentity(source);
      const currentBaseIsSideSpecific = rowHasSideSpecificIdentity(bucket.base);

      if (sourceIsPlainReference || currentBaseIsSideSpecific) {
        bucket.base = source;
      }

      bucket.sides[side] = source;

      if (rowHasPlacement(source)) {
        bucket.placementSources.push(source);
      }
    });

    const records = Array.from(grouped.values())
      .map((bucket) => {
        const baseSource = bucket.base || {};
        const frontSource = bucket.sides.Front || baseSource;
        const backSource = bucket.sides.Back || baseSource;
        const leftSource = bucket.sides.Left || baseSource;
        const rightSource = bucket.sides.Right || baseSource;

        const baseRecord = getAdminApparelRecordForDesign(
          Object.assign({}, design, {
            mockup: baseSource,
            defaultMockup: null,
            primaryMockup: null,
            productStyleReference: null,
            productReference: null,
            apparelReference: null,
            defaultApparel: null,
            apparel: null,
            mockups: []
          })
        );

        if (!baseRecord) return null;

        const frontImage =
          mockupUrlCandidates(
            rowImageForSide(frontSource, "Front"),
            rowImageForSide(baseSource, "Front"),
            baseRecord.front
          )[0] || "";

        const backImage =
          mockupUrlCandidates(
            rowImageForSide(backSource, "Back"),
            rowImageForSide(baseSource, "Back"),
            baseRecord.back
          )[0] || "";

        const leftImage =
          mockupUrlCandidates(
            rowImageForSide(leftSource, "Left"),
            rowImageForSide(baseSource, "Left"),
            baseRecord.left
          )[0] || "";

        const rightImage =
          mockupUrlCandidates(
            rowImageForSide(rightSource, "Right"),
            rowImageForSide(baseSource, "Right"),
            baseRecord.right
          )[0] || "";

        const frontPlacementSource =
          bucket.placementSources.find((item) => getRowSide(item) === "Front") ||
          null;

        const placementSource =
          frontPlacementSource ||
          bucket.placementSources[0] ||
          baseSource;

        return Object.assign({}, baseRecord, {
          front: frontImage,
          back: backImage,
          left: leftImage,
          right: rightImage,

          frontImageUrl: frontImage,
          backImageUrl: backImage,
          leftImageUrl: leftImage,
          rightImageUrl: rightImage,

          views: getAdminEnabledSidesForDesign(design).join(", ") || baseRecord.views || "Front",

          placementSkuKey:
            placementSource.placementSkuKey ||
            baseRecord.placementSkuKey ||
            "",

          placementLabel:
            placementSource.placementLabel ||
            baseRecord.placementLabel ||
            "",

          gridPlacement:
            placementSource.gridPlacement ||
            placementSource.grid ||
            baseRecord.gridPlacement ||
            baseRecord.grid ||
            null,

          modalPlacement:
            placementSource.modalPlacement ||
            placementSource.modal ||
            baseRecord.modalPlacement ||
            baseRecord.modal ||
            null,

          adminConfigured: true
        });
      })
      .filter(Boolean);

    const defaultKey = String(design.defaultApparelKey || "").trim();
    const unique = uniqueApparelRecords(records);

    if (defaultKey) {
      unique.sort((a, b) => {
        const aMatch = apparelRecordKey(a) === defaultKey ? -1 : 0;
        const bMatch = apparelRecordKey(b) === defaultKey ? -1 : 0;
        return aMatch - bMatch;
      });
    }

    return unique;
  }

  function getCombinedApparelLibrary() {
    /*
       v84:
       Do not merge the built-in CURATED_APPAREL_LIBRARY anymore.

       The old hardcoded list contained legacy apparel such as Gildan 2000.
       That caused removed apparel to keep appearing in the public visualizer
       even when the Admin Approved Apparel Library no longer contained it.

       Public apparel sources are now:
       - Design/admin-saved mockups first
       - Matrix curatedApparel only if the matrix intentionally provides it
    */
    const records = [];

    (MATRIX_APPAREL_LIBRARY || []).forEach((record) => {
      if (!record) return;

      const exists = records.some(
        (item) =>
          String(item.productId || "") === String(record.productId || "") &&
          String(item.styleId || "") === String(record.styleId || "") &&
          String(item.colorName || "") === String(record.colorName || "")
      );

      if (!exists) records.push(record);
    });

    return records;
  }

  function buildMatrixIndex(matrix) {
    DESIGN_MATRIX_BY_ID = new Map();
    DESIGN_MATRIX_BY_TITLE = new Map();
    MATRIX_APPAREL_LIBRARY = [];

    if (!matrix || typeof matrix !== "object") return;

    if (Array.isArray(matrix.artworks)) {
      matrix.artworks.forEach((artwork) => {
        const designId = String(
          artwork.designId ||
            artwork.inksoftDesignId ||
            artwork.inksoft_design_id ||
            ""
        ).trim();

        if (designId) {
          DESIGN_MATRIX_BY_ID.set(designId, artwork);
        }

        getMatrixLookupTitles(artwork).forEach((titleKey) => {
          if (titleKey && !DESIGN_MATRIX_BY_TITLE.has(titleKey)) {
            DESIGN_MATRIX_BY_TITLE.set(titleKey, artwork);
          }
        });
      });
    }

    if (Array.isArray(matrix.curatedApparel)) {
      MATRIX_APPAREL_LIBRARY = matrix.curatedApparel
        .map(normalizeMatrixApparelRecord)
        .filter(Boolean);
    }
  }

  async function loadDesignMatrix() {
    const useMatrix = (matrix, sourceLabel) => {
      DESIGN_MATRIX = matrix;
      buildMatrixIndex(matrix);
      console.info(
        `[Design Inspiration] Matrix loaded from ${sourceLabel}:`,
        matrix?.counts || {
          artworks: Array.isArray(matrix?.artworks) ? matrix.artworks.length : 0,
          curatedApparel: Array.isArray(matrix?.curatedApparel)
            ? matrix.curatedApparel.length
            : 0,
        },
        {
          matrixUrl: DI_MATRIX_URL,
          indexedDesignIds: DESIGN_MATRIX_BY_ID.size,
          indexedTitles: DESIGN_MATRIX_BY_TITLE.size,
          barackObamaPosition:
            DESIGN_MATRIX_BY_ID.get("1752537")?.sides?.Front?.position || null,
        }
      );
      return matrix;
    };

    if (!DI_MATRIX_URL) {
      console.warn(
        "[Design Inspiration] Matrix URL is blank. Matrix rules will not be applied."
      );
      DESIGN_MATRIX = null;
      DESIGN_MATRIX_BY_ID = new Map();
      DESIGN_MATRIX_BY_TITLE = new Map();
      MATRIX_APPAREL_LIBRARY = [];
      return null;
    }

    try {
      const matrix = await fetchJson(DI_MATRIX_URL);
      return useMatrix(matrix, "external JSON");
    } catch (error) {
      console.warn(
        "[Design Inspiration] Matrix JSON could not be loaded. Matrix rules will not be applied:",
        error
      );

      DESIGN_MATRIX = null;
      DESIGN_MATRIX_BY_ID = new Map();
      DESIGN_MATRIX_BY_TITLE = new Map();
      MATRIX_APPAREL_LIBRARY = [];
      return null;
    }
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function normalizeImage(url) {
    if (!url) return "";

    const raw = String(url).trim();
    if (!raw) return "";

    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;

    if (raw.startsWith("/Unisource_Apparel/")) {
      return `https://shop.unisourceapparel.com${raw}`;
    }

    if (raw.startsWith("/images/")) {
      return `https://shop.unisourceapparel.com/Unisource_Apparel${raw}`;
    }

    if (raw.startsWith("Unisource_Apparel/")) {
      return `https://shop.unisourceapparel.com/${raw}`;
    }

    return `https://shop.unisourceapparel.com/${raw.replace(/^\/+/, "")}`;
  }

  function extractDesignId(design) {
    if (design.inksoftDesignId) return String(design.inksoftDesignId);

    const sources = [
      design.id,
      design.slug,
      design.thumbnailUrl,
      design.previewImageUrl,
      design.imageUrl,
      Array.isArray(design.canvases) && design.canvases[0]
        ? design.canvases[0].thumbnailUrl
        : "",
      Array.isArray(design.canvases) && design.canvases[0]
        ? design.canvases[0].imageUrl
        : "",
    ];

    for (const src of sources) {
      const value = String(src || "");

      let match = value.match(/DI-(\d+)/i);
      if (match && match[1]) return match[1];

      match = value.match(/designs\/(\d+)\./i);
      if (match && match[1]) return match[1];

      match = value.match(/2959-(\d+)/i);
      if (match && match[1]) return match[1];

      match = value.match(/-(\d{6,})$/i);
      if (match && match[1]) return match[1];
    }

    return "";
  }

  function getMatrixArtworkForDesign(design) {
    if (!design) return null;

    const hasIdIndex = DESIGN_MATRIX_BY_ID && DESIGN_MATRIX_BY_ID.size;
    const hasTitleIndex = DESIGN_MATRIX_BY_TITLE && DESIGN_MATRIX_BY_TITLE.size;

    if (!hasIdIndex && !hasTitleIndex) return null;

    const designId = extractDesignId(design);
    if (designId && DESIGN_MATRIX_BY_ID.has(String(designId))) {
      return DESIGN_MATRIX_BY_ID.get(String(designId));
    }

    const directId = String(
      design.inksoftDesignId ||
        design.inksoft_design_id ||
        design.designId ||
        design.id ||
        ""
    ).trim();

    if (directId && DESIGN_MATRIX_BY_ID.has(directId)) {
      return DESIGN_MATRIX_BY_ID.get(directId);
    }

    const titleKeys = getDesignLookupTitles(design);

    for (const titleKey of titleKeys) {
      if (DESIGN_MATRIX_BY_TITLE.has(titleKey)) {
        return DESIGN_MATRIX_BY_TITLE.get(titleKey);
      }
    }

    return null;
  }

  function normalizeMatrixSideRecord(record, side) {
    if (!record) return null;

    return {
      side: side || normalizeSideName(record.side),
      displaySide: Boolean(record.displaySide),
      sideArtworkSource: record.sideArtworkSource || "Auto InkSoft",
      artworkUrlOverride: record.artworkUrlOverride || "",
      mockupUrlOverride: record.mockupUrlOverride || "",
      placementLabel: record.placementLabel || record.placementPreset || side || "",
      placementPreset: record.placementPreset || "",

      /* v46 naming convention. JSON is the source of positioning. */
      artworkPositionModal:
        record.artworkPositionModal || record.position || record.placementOverride || {},
      artworkPositionGrid:
        record.artworkPositionGrid ||
        record.artworkPositionModal ||
        record.position ||
        record.placementOverride ||
        {},
      apparelPositionModal:
        record.apparelPositionModal ||
        record.apparelPosition ||
        record.mockupPosition ||
        record.garmentPosition ||
        {},
      apparelPositionGrid:
        record.apparelPositionGrid ||
        record.apparelPositionModal ||
        record.apparelPosition ||
        record.mockupPosition ||
        record.garmentPosition ||
        {},

      /* Legacy fields retained only for backward compatibility. */
      placementOverride: record.placementOverride || {},
      position: record.position || {},
      placementReady: Boolean(record.placementReady),
      notes: record.notes || "",
    };
  }

  function getMatrixSidesForDesign(design) {
    const matrixArtwork = getMatrixArtworkForDesign(design);
    if (!matrixArtwork || !matrixArtwork.sides) return null;

    const result = {};

    ["Front", "Back", "Left", "Right"].forEach((side) => {
      const record = matrixArtwork.sides[side];
      if (record) {
        result[side] = normalizeMatrixSideRecord(record, side);
      }
    });

    return result;
  }

  function getMatrixSideForDesign(design, side) {
    const sides = getMatrixSidesForDesign(design);
    const normalizedSide = normalizeSideName(side);

    return sides && sides[normalizedSide] ? sides[normalizedSide] : null;
  }

  function getMatrixPlacementPresetDefaults(presetName) {
    const name = String(presetName || "").trim();

    if (!name || !DESIGN_MATRIX || !DESIGN_MATRIX.placementPresets) {
      return {};
    }

    return DESIGN_MATRIX.placementPresets[name] || {};
  }

  function hasPositionValue(value) {
    return value !== null && value !== undefined && value !== "";
  }

  function firstPositionValue(...values) {
    for (const value of values) {
      if (hasPositionValue(value)) return value;
    }
    return undefined;
  }

  function normalizeArtworkPositionObject(source) {
    const item = source || {};

    return {
      topPercent: firstPositionValue(item.topPercent, item.artworkYPercent),
      leftPercent: firstPositionValue(item.leftPercent, item.artworkXPercent),
      artworkWidthPercent: firstPositionValue(item.artworkWidthPercent, item.widthPercent),
      maxWidthPx: firstPositionValue(item.maxWidthPx, item.artworkMaxWidthPx),
      maxHeightPx: firstPositionValue(item.maxHeightPx, item.artworkMaxHeightPx),
      translateXPercent: firstPositionValue(item.translateXPercent, item.translateX, -50),
      translateYPercent: firstPositionValue(item.translateYPercent, item.translateY, -50),
      rotationDeg: firstPositionValue(item.rotationDeg, 0),
    };
  }

  function normalizeApparelPositionObject(source) {
    const item = source || {};

    return {
      topPercent: firstPositionValue(item.topPercent, item.apparelTopPercent, item.mockupTopPercent),
      leftPercent: firstPositionValue(item.leftPercent, item.apparelLeftPercent, item.mockupLeftPercent),
      widthPercent: firstPositionValue(item.widthPercent, item.apparelWidthPercent, item.mockupWidthPercent),
      heightPercent: firstPositionValue(item.heightPercent, item.apparelHeightPercent, item.mockupHeightPercent),
      maxWidthPx: firstPositionValue(item.maxWidthPx, item.apparelMaxWidthPx, item.mockupMaxWidthPx),
      maxHeightPx: firstPositionValue(item.maxHeightPx, item.apparelMaxHeightPx, item.mockupMaxHeightPx),
      translateXPercent: firstPositionValue(item.translateXPercent, item.translateX),
      translateYPercent: firstPositionValue(item.translateYPercent, item.translateY),
      scale: firstPositionValue(item.scale),
      rotationDeg: firstPositionValue(item.rotationDeg, 0),
    };
  }

  function mergePositionObjects(...sources) {
    const result = {};

    sources.forEach((source) => {
      if (!source) return;
      Object.keys(source).forEach((key) => {
        if (!hasPositionValue(result[key]) && hasPositionValue(source[key])) {
          result[key] = source[key];
        }
      });
    });

    return result;
  }

  function getFallbackPreset(name) {
    return DESIGN_MATRIX?.fallbackPresets?.[name] || {};
  }

  function getFallbackPresetBySide(name, side) {
    const normalizedSide = normalizeSideName(side);
    return DESIGN_MATRIX?.fallbackPresets?.[name]?.[normalizedSide] || {};
  }

  function getMatrixPlacementPresetPosition(presetName, viewMode) {
    const preset = getMatrixPlacementPresetDefaults(presetName);
    const modeKey = viewMode === "Grid" ? "artworkPositionGrid" : "artworkPositionModal";
    const otherModeKey = viewMode === "Grid" ? "artworkPositionModal" : "artworkPositionGrid";

    return normalizeArtworkPositionObject(
      preset[modeKey] || preset[otherModeKey] || preset
    );
  }

  function getApparelPositionPreset(apparelRecord, side, viewMode) {
    const presets = DESIGN_MATRIX?.apparelPositionPresets || {};
    if (!apparelRecord || !presets) return {};

    const normalizedSide = normalizeSideName(side);
    const modeKey = viewMode === "Grid" ? "apparelPositionGrid" : "apparelPositionModal";
    const multiModeKey = viewMode === "Grid" ? "apparelPositionGridMultiSide" : "apparelPositionModalMultiSide";

    const candidateKeys = uniqueList([
      apparelRecord.category,
      apparelRecord.type,
      apparelRecord.mfgSku,
      apparelRecord.productSku,
      apparelRecord.productId,
      apparelRecord.key,
    ].filter(Boolean).map((item) => String(item)));

    for (const key of candidateKeys) {
      const entry = presets[key];
      if (!entry) continue;

      const sideEntry = entry[normalizedSide] || entry.Default || entry.default || entry;
      const resolved =
        sideEntry[multiModeKey] ||
        sideEntry[modeKey] ||
        sideEntry.apparelPositionModal ||
        sideEntry.apparelPositionGrid ||
        sideEntry;

      if (resolved && Object.keys(resolved).length) {
        return normalizeApparelPositionObject(resolved);
      }
    }

    return {};
  }

  function buildArtworkPositionStyle(position) {
    const styles = [];

    if (hasPositionValue(position.leftPercent)) {
      styles.push(`left: ${Number(position.leftPercent)}% !important`);
    }

    if (hasPositionValue(position.topPercent)) {
      styles.push(`top: ${Number(position.topPercent)}% !important`);
    }

    if (hasPositionValue(position.artworkWidthPercent)) {
      styles.push(`width: ${Number(position.artworkWidthPercent)}% !important`);
    }

    if (hasPositionValue(position.maxWidthPx)) {
      if (String(position.maxWidthPx).toLowerCase() === "none") {
        styles.push("max-width: none !important");
      } else {
        styles.push(`max-width: ${Number(position.maxWidthPx)}px !important`);
      }
    }

    if (hasPositionValue(position.maxHeightPx)) {
      if (String(position.maxHeightPx).toLowerCase() === "none") {
        styles.push("max-height: none !important");
      } else {
        styles.push(`max-height: ${Number(position.maxHeightPx)}px !important`);
      }
    }

    const translateX = firstPositionValue(position.translateXPercent, -50);
    const translateY = firstPositionValue(position.translateYPercent, -50);
    const rotation = firstPositionValue(position.rotationDeg, 0);

    styles.push(
      `transform: translate(${Number(translateX)}%, ${Number(translateY)}%) rotate(${Number(rotation) || 0}deg) !important`
    );

    return styles.length ? styles.join("; ") + ";" : "";
  }

  function buildApparelPositionStyle(position) {
    const styles = [];

    styles.push("position: absolute !important");

    if (hasPositionValue(position.leftPercent)) {
      styles.push(`left: ${Number(position.leftPercent)}% !important`);
    }

    if (hasPositionValue(position.topPercent)) {
      styles.push(`top: ${Number(position.topPercent)}% !important`);
    }

    if (hasPositionValue(position.widthPercent)) {
      styles.push(`width: ${Number(position.widthPercent)}% !important`);
    }

    if (hasPositionValue(position.heightPercent)) {
      styles.push(`height: ${Number(position.heightPercent)}% !important`);
    } else {
      styles.push("height: auto !important");
    }

    if (hasPositionValue(position.maxWidthPx)) {
      if (String(position.maxWidthPx).toLowerCase() === "none") {
        styles.push("max-width: none !important");
      } else {
        styles.push(`max-width: ${Number(position.maxWidthPx)}px !important`);
      }
    }

    if (hasPositionValue(position.maxHeightPx)) {
      if (String(position.maxHeightPx).toLowerCase() === "none") {
        styles.push("max-height: none !important");
      } else {
        styles.push(`max-height: ${Number(position.maxHeightPx)}px !important`);
      }
    }

    const transformParts = [];

    if (hasPositionValue(position.translateXPercent) || hasPositionValue(position.translateYPercent)) {
      transformParts.push(
        `translate(${Number(firstPositionValue(position.translateXPercent, 0))}%, ${Number(firstPositionValue(position.translateYPercent, 0))}%)`
      );
    }

    if (hasPositionValue(position.scale)) {
      transformParts.push(`scale(${Number(position.scale)})`);
    }

    if (hasPositionValue(position.rotationDeg) && Number(position.rotationDeg)) {
      transformParts.push(`rotate(${Number(position.rotationDeg)}deg)`);
    }

    if (transformParts.length) {
      styles.push(`transform: ${transformParts.join(" ")} !important`);
      styles.push("transform-origin: center center !important");
    }

    return styles.length ? styles.join("; ") + ";" : "";
  }

  function normalizeExplicitArtworkOverride(source) {
    if (!source || typeof source !== "object") return null;

    const result = {};
    const mappings = {
      topPercent: ["topPercent", "artworkYPercent"],
      leftPercent: ["leftPercent", "artworkXPercent"],
      artworkWidthPercent: ["artworkWidthPercent", "widthPercent"],
      maxWidthPx: ["maxWidthPx", "artworkMaxWidthPx"],
      maxHeightPx: ["maxHeightPx", "artworkMaxHeightPx"],
      translateXPercent: ["translateXPercent", "translateX"],
      translateYPercent: ["translateYPercent", "translateY"],
      rotationDeg: ["rotationDeg"],
    };

    Object.entries(mappings).forEach(([target, keys]) => {
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(source, key) && hasPositionValue(source[key])) {
          result[target] = source[key];
          break;
        }
      }
    });

    return Object.keys(result).length ? result : null;
  }

  function normalizeExplicitApparelOverride(source) {
    if (!source || typeof source !== "object") return null;

    const result = {};
    const mappings = {
      topPercent: ["topPercent", "apparelTopPercent", "mockupTopPercent"],
      leftPercent: ["leftPercent", "apparelLeftPercent", "mockupLeftPercent"],
      widthPercent: ["widthPercent", "apparelWidthPercent", "mockupWidthPercent"],
      heightPercent: ["heightPercent", "apparelHeightPercent", "mockupHeightPercent"],
      maxWidthPx: ["maxWidthPx", "apparelMaxWidthPx", "mockupMaxWidthPx"],
      maxHeightPx: ["maxHeightPx", "apparelMaxHeightPx", "mockupMaxHeightPx"],
      translateXPercent: ["translateXPercent", "translateX"],
      translateYPercent: ["translateYPercent", "translateY"],
      scale: ["scale"],
      rotationDeg: ["rotationDeg"],
    };

    Object.entries(mappings).forEach(([target, keys]) => {
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(source, key) && hasPositionValue(source[key])) {
          result[target] = source[key];
          break;
        }
      }
    });

    return Object.keys(result).length ? result : null;
  }

  function getVisualizerProductOverrideSource(matrixArtwork, apparelRecord, side, modeKey) {
    if (!matrixArtwork || !apparelRecord) return null;

    const productId = String(apparelRecord.productId || "");
    const styleId = String(apparelRecord.styleId || "");
    if (!productId) return null;

    const products = matrixArtwork?.visualizerControls?.productOverrides;
    const productBucket = products?.[productId];
    if (!productBucket) return null;

    const styles = productBucket.styles || {};
    const styleBucket =
      (styleId && styles[styleId]) ||
      styles._default ||
      styles.default ||
      null;

    const sideBucket = styleBucket?.sides?.[side];
    if (!sideBucket) return null;

    return sideBucket?.[modeKey] || null;
  }

  function getArtworkPositionOverride(matrixArtwork, apparelRecord, side, viewMode) {
    const modeKey = viewMode === "Grid" ? "artworkPositionGrid" : "artworkPositionModal";

    return normalizeExplicitArtworkOverride(
      getVisualizerProductOverrideSource(
        matrixArtwork,
        apparelRecord,
        side,
        modeKey
      )
    );
  }

  function getApparelPositionOverrideSource(source, apparelRecord, side, modeKey) {
    if (!source || !apparelRecord) return null;

    const productId = String(apparelRecord.productId || "");
    const styleId = String(apparelRecord.styleId || "");
    if (!productId) return null;

    const productBucket = source[productId];
    if (!productBucket) return null;

    const styleBucket =
      (styleId && productBucket[styleId]) ||
      productBucket._default ||
      productBucket.default ||
      null;

    const sideBucket = styleBucket?.[side];
    if (!sideBucket) return null;

    return normalizeApparelPositionObject(sideBucket?.[modeKey]);
  }

  function getApparelPositionOverride(matrixArtwork, apparelRecord, side, viewMode) {
    const modeKey = viewMode === "Grid" ? "apparelPositionGrid" : "apparelPositionModal";

    return mergePositionObjects(
      normalizeExplicitApparelOverride(
        getVisualizerProductOverrideSource(
          matrixArtwork,
          apparelRecord,
          side,
          modeKey
        )
      ),
      getApparelPositionOverrideSource(matrixArtwork?.apparelPositionOverrides, apparelRecord, side, modeKey),
      getApparelPositionOverrideSource(DESIGN_MATRIX?.apparelPositionOverrides, apparelRecord, side, modeKey)
    );
  }


  function normalizeSkuPlacementKey(value) {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ");
  }

  function getPlacementSkuCandidateKeys(apparelRecord) {
    if (!apparelRecord) return [];

    const productId = normalizeSkuPlacementKey(apparelRecord.productId);
    const styleId = normalizeSkuPlacementKey(apparelRecord.styleId);
    const sku = normalizeSkuPlacementKey(
      apparelRecord.manufacturerSku ||
        apparelRecord.mfgSku ||
        apparelRecord.sku ||
        apparelRecord.productSku ||
        ""
    );

    return uniqueList([
      apparelRecord.placementSkuKey,
      [productId, styleId, sku].join("|"),
      [productId, styleId, ""].join("|"),
      [productId, "", sku].join("|"),
      ["", styleId, sku].join("|"),
    ].filter((key) => String(key || "").replace(/\|/g, "").trim()));
  }

  function getAdminSkuPlacementEntry(design, apparelRecord) {
    if (!design || !apparelRecord) return null;

    const map = design.placementBySku && typeof design.placementBySku === "object"
      ? design.placementBySku
      : {};

    const candidateKeys = getPlacementSkuCandidateKeys(apparelRecord);

    for (const key of candidateKeys) {
      if (map[key]) return map[key];
    }

    /*
       v85:
       Some Admin payloads also place the resolved grid/modal placement on the
       apparel row or mockup row itself. Use that as a secondary source. This
       still remains artwork-specific because these rows live inside the current
       design record, not inside the global Approved Apparel Library.
    */
    if (apparelRecord.gridPlacement || apparelRecord.modalPlacement || apparelRecord.grid || apparelRecord.modal) {
      return apparelRecord;
    }

    return null;
  }

  function getVisualPlacementSideKey(side) {
    const text = String(side || "Front").trim().toLowerCase();
    if (text.includes("back")) return "Back";
    if (text.includes("right")) return "Right";
    if (text.includes("left")) return "Left";
    return "Front";
  }

  function getVisualPlacementForMode(entry, viewMode, side) {
    if (!entry || typeof entry !== "object") return null;
    const key = viewMode === "Grid" ? "grid" : "modal";
    const altKey = viewMode === "Grid" ? "gridPlacement" : "modalPlacement";
    const sideKey = getVisualPlacementSideKey(side);

    const sides = entry.sides && typeof entry.sides === "object" ? entry.sides : null;
    const sideEntry = sides
      ? (sides[sideKey] || sides[sideKey.toLowerCase()] || sides[sideKey.toUpperCase()])
      : null;

    if (sideEntry && typeof sideEntry === "object") {
      return sideEntry[key] || sideEntry[altKey] || null;
    }

    return entry[key] || entry[altKey] || null;
  }

  function normalizeAdminArtworkPlacement(entry, viewMode, side) {
    const source = getVisualPlacementForMode(entry, viewMode, side);
    if (!source || typeof source !== "object") return null;

    /*
      v87:
      Admin SKU placement is an explicit override, not another fallback preset.
      When Admin placement exists, disable legacy max constraints so grid/modal
      values visibly control artwork size and position.
    */
    const result = {
      leftPercent: firstPositionValue(source.artworkX, source.artworkXPct, source.xPct, source.leftPercent),
      topPercent: firstPositionValue(source.artworkY, source.artworkYPct, source.yPct, source.topPercent),
      artworkWidthPercent: firstPositionValue(source.artworkWidth, source.artworkWidthPct, source.scalePct, source.widthPercent),
      maxWidthPx: "none",
      maxHeightPx: "none",
      translateXPercent: firstPositionValue(source.translateXPercent, source.translateX, -50),
      translateYPercent: firstPositionValue(source.translateYPercent, source.translateY, -50),
      rotationDeg: firstPositionValue(source.artworkRotation, source.artworkRotationDeg, source.rotationDeg, 0),
      adminPlacementApplied: true,
    };

    return Object.keys(result).some((key) => hasPositionValue(result[key])) ? result : null;
  }

  function normalizeAdminApparelPlacement(entry, viewMode, side) {
    const source = getVisualPlacementForMode(entry, viewMode, side);
    if (!source || typeof source !== "object") return null;

    /*
      v87:
      Apparel Scale is treated as the actual mockup width percentage.
      Do not merge the old matrix/default apparel width/max constraints after
      this, otherwise the public visualizer appears to keep the default layout.
    */
    const result = {
      leftPercent: firstPositionValue(source.apparelX, source.apparelXPct, source.leftPercent),
      topPercent: firstPositionValue(source.apparelY, source.apparelYPct, source.topPercent),
      widthPercent: firstPositionValue(source.apparelScale, source.apparelScalePct, source.apparelWidthPercent, source.widthPercent),
      heightPercent: firstPositionValue(source.apparelHeight, source.apparelHeightPct, source.heightPercent),
      maxWidthPx: "none",
      maxHeightPx: "none",
      translateXPercent: firstPositionValue(source.translateXPercent, source.translateX, -50),
      translateYPercent: firstPositionValue(source.translateYPercent, source.translateY, -50),
      rotationDeg: firstPositionValue(source.apparelRotation, source.apparelRotationDeg, source.rotationDeg, 0),
      adminPlacementApplied: true,
    };

    return Object.keys(result).some((key) => hasPositionValue(result[key])) ? result : null;
  }

  function getMatrixApparelPositionStyle(design, side, viewMode = "Modal", apparelRecord = null) {
    const matrixArtwork = getMatrixArtworkForDesign(design);
    const matrixSide = getMatrixSideForDesign(design, side);
    const modeKey = viewMode === "Grid" ? "apparelPositionGrid" : "apparelPositionModal";
    const fallbackKey = viewMode === "Grid" ? "defaultApparelPositionGrid" : "defaultApparelPositionModal";
    const fallbackSideKey = viewMode === "Grid" ? "defaultApparelPositionGridBySide" : "defaultApparelPositionModalBySide";

    /*
       v57:
       Additional apparel can now have its own display positioning.

       Registered artwork priority:
       1. artwork.apparelPositionOverrides[productId][styleId][side]
       2. artwork.apparelPositionOverrides[productId]._default[side]
       3. DESIGN_MATRIX.apparelPositionOverrides[productId][styleId][side]
       4. DESIGN_MATRIX.apparelPositionOverrides[productId]._default[side]
       5. side.apparelPositionModal OR side.apparelPositionGrid
       6. apparelPositionPresets for the active apparel type and side
       7. fallbackPresets

       This preserves current side-level controls, but lets an added hoodie,
       crewneck, polo, or other product have its own modal/grid display tuning.
    */
    const adminSkuPlacement = getAdminSkuPlacementEntry(design, apparelRecord);
    const adminApparelPlacement = normalizeAdminApparelPlacement(adminSkuPlacement, viewMode, side);

    if (adminApparelPlacement) {
      return buildApparelPositionStyle(adminApparelPlacement);
    }

    const position = matrixArtwork
      ? mergePositionObjects(
          getApparelPositionOverride(matrixArtwork, apparelRecord, side, viewMode),
          normalizeApparelPositionObject(matrixSide?.[modeKey]),
          getApparelPositionPreset(apparelRecord, side, viewMode),
          normalizeApparelPositionObject(getFallbackPresetBySide(fallbackSideKey, side)),
          normalizeApparelPositionObject(getFallbackPreset(fallbackKey))
        )
      : mergePositionObjects(
          normalizeApparelPositionObject(getFallbackPresetBySide(fallbackSideKey, side)),
          normalizeApparelPositionObject(getFallbackPreset(fallbackKey)),
          getApparelPositionPreset(apparelRecord, side, viewMode)
        );

    return buildApparelPositionStyle(position);
  }

  function getMatrixPlacementStyle(design, side, viewMode = "Modal", apparelRecord = null) {
    const matrixArtwork = getMatrixArtworkForDesign(design);
    const matrixSide = getMatrixSideForDesign(design, side);
    const modeKey = viewMode === "Grid" ? "artworkPositionGrid" : "artworkPositionModal";
    const otherModeKey = viewMode === "Grid" ? "artworkPositionModal" : "artworkPositionGrid";
    const fallbackKey = viewMode === "Grid" ? "defaultArtworkPositionGrid" : "defaultArtworkPositionModal";
    const fallbackSideKey = viewMode === "Grid" ? "defaultArtworkPositionGridBySide" : "defaultArtworkPositionModalBySide";
    const presetName = matrixSide?.placementPreset || matrixSide?.placementLabel || "Full Front";

    /*
       v48:
       Registered JSON artwork keeps the normal priority:
       side override → placement preset → fallback.

       Unregistered / unmapped InkSoft artwork now uses fallbackPresets first.
       This means editing:
         fallbackPresets.defaultArtworkPositionGrid
       will affect grid cards that are not yet in artworks[].
    */
    const adminSkuPlacement = getAdminSkuPlacementEntry(design, apparelRecord);
    const adminArtworkPlacement = normalizeAdminArtworkPlacement(adminSkuPlacement, viewMode, side);

    if (adminArtworkPlacement) {
      return buildArtworkPositionStyle(adminArtworkPlacement);
    }

    const position = matrixArtwork
      ? mergePositionObjects(
          getArtworkPositionOverride(matrixArtwork, apparelRecord, side, viewMode),
          normalizeArtworkPositionObject(matrixSide?.[modeKey]),
          normalizeArtworkPositionObject(matrixSide?.[otherModeKey]),
          normalizeArtworkPositionObject(matrixSide?.position),
          normalizeArtworkPositionObject(matrixSide?.placementOverride),
          getMatrixPlacementPresetPosition(presetName, viewMode),
          normalizeArtworkPositionObject(getFallbackPresetBySide(fallbackSideKey, side)),
          normalizeArtworkPositionObject(getFallbackPreset(fallbackKey))
        )
      : mergePositionObjects(
          normalizeArtworkPositionObject(getFallbackPresetBySide(fallbackSideKey, side)),
          normalizeArtworkPositionObject(getFallbackPreset(fallbackKey)),
          getMatrixPlacementPresetPosition(presetName, viewMode)
        );

    return buildArtworkPositionStyle(position);
  }

  function applyMatrixToDesign(design) {
    if (!design) return design;

    const matrixArtwork = getMatrixArtworkForDesign(design);
    if (!matrixArtwork) return design;

    const merged = {
      ...design,
      matrixArtwork,
      defaultProductId:
        matrixArtwork.defaultApparel?.productId ||
        design.defaultProductId ||
        design.default_product_id,
      default_product_id:
        matrixArtwork.defaultApparel?.productId ||
        design.default_product_id ||
        design.defaultProductId,
      defaultStyleId:
        matrixArtwork.defaultApparel?.styleId ||
        design.defaultStyleId ||
        design.default_style_id,
      default_style_id:
        matrixArtwork.defaultApparel?.styleId ||
        design.default_style_id ||
        design.defaultStyleId,
      defaultColor:
        matrixArtwork.defaultApparel?.color ||
        design.defaultColor ||
        design.default_color,
      default_color:
        matrixArtwork.defaultApparel?.color ||
        design.default_color ||
        design.defaultColor,
    };

    const allowedProductIds = new Set();

    Object.values(matrixArtwork.allowedApparel || {}).forEach((item) => {
      (item.productIds || []).forEach((productId) => {
        if (productId) allowedProductIds.add(String(productId));
      });
    });

    if (matrixArtwork.defaultApparel?.productId) {
      allowedProductIds.add(String(matrixArtwork.defaultApparel.productId));
    }

    if (allowedProductIds.size) {
      merged.allowedProductIds = Array.from(allowedProductIds);
      merged.allowed_product_ids = Array.from(allowedProductIds);
    }

    const sides = matrixArtwork.sides || {};
    const artworkViews = ["Front", "Back", "Left", "Right"]
      .map((side) => sides[side])
      .filter((sideRecord) => sideRecord && sideRecord.displaySide)
      .map((sideRecord) => {
        const side = normalizeSideName(sideRecord.side);
        return {
          side,
          label: sideRecord.placementLabel || sideRecord.placementPreset || side,
          artworkUrl: sideRecord.artworkUrlOverride || "",
          imageUrl: sideRecord.artworkUrlOverride || "",
          url: sideRecord.artworkUrlOverride || "",
          matrixSide: sideRecord,
        };
      });

    if (artworkViews.length) {
      merged.artworkViews = artworkViews;
    }

    return merged;
  }

  function getPublisherArtwork(design) {
    const designId = extractDesignId(design);
    if (!designId) return "";

    return `https://cdn.inksoft.com/images/publishers/2959/designs/${designId}.front.480.png`;
  }

  function getThumbnailArtwork(design) {
    const thumbnail = normalizeImage(design.thumbnailUrl);

    const canvasThumb =
      Array.isArray(design.canvases) && design.canvases.length
        ? normalizeImage(design.canvases[0].thumbnailUrl)
        : "";

    return thumbnail || canvasThumb;
  }

  function getBestArtwork(design) {
    return getPublisherArtwork(design) || getThumbnailArtwork(design);
  }

  function buildArtworkImage(design, className) {
    const artwork = getBestArtwork(design);
    const fallback = getThumbnailArtwork(design);

    if (!artwork && !fallback) {
      return `<div class="di-product-mockup__placeholder">No Artwork</div>`;
    }

    return `
      <img
        class="${escapeHtml(className)}"
        src="${escapeHtml(artwork || fallback)}"
        alt="${escapeHtml(design.title)} artwork"
        loading="lazy"
        data-fallback-src="${escapeHtml(fallback)}"
        onerror="
          if (this.dataset.fallbackSrc && this.src !== this.dataset.fallbackSrc) {
            this.src = this.dataset.fallbackSrc;
          } else {
            this.style.display = 'none';
          }
        "
      >
    `;
  }


  function normalizeSideName(value) {
    const raw = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");

    if (!raw) return "Front";
    if (raw.includes("back")) return "Back";
    if (raw.includes("right")) return "Right";
    if (raw.includes("left")) return "Left";
    if (raw.includes("front")) return "Front";

    return "Front";
  }

  function isInksoftTransparentArtworkUrl(url) {
    const value = String(url || "").trim().toLowerCase();
    return (
      !!value &&
      value.includes("cdn.inksoft.com/images/publishers/") &&
      value.includes("/designs/") &&
      value.endsWith(".png")
    );
  }

  function isNonFrontPublisherArtworkUrl(url, side) {
    /*
      v99:
      InkSoft publisher side PNGs for Back/Left/Right are guessed/generated URLs.
      They can revive older rendered side artwork, like the Lawrence "data" back
      graphic, even after the Admin panel no longer intends to use that artwork.

      Only Front may use publisher fallback by default. Non-front artwork must
      come from a deliberate matrix/admin override, not a generated publisher URL.
    */
    const normalizedSide = normalizeSideName(side);
    if (normalizedSide === "Front") return false;
    return isInksoftTransparentArtworkUrl(url);
  }

  function getArtworkUrlFromCanvas(canvas) {
    if (!canvas) return "";

    return normalizeImage(
      canvas.transparentSvgUrl ||
        canvas.transparentImageUrl ||
        canvas.transparentUrl ||
        canvas.svgUrl ||
        canvas.imageUrl ||
        canvas.thumbnailUrl ||
        canvas.previewImageUrl ||
        canvas.url ||
        ""
    );
  }

  function getCanvasFallbackUrl(canvas) {
    if (!canvas) return "";

    return normalizeImage(
      canvas.imageUrl ||
        canvas.thumbnailUrl ||
        canvas.previewImageUrl ||
        canvas.url ||
        ""
    );
  }

  function inferSideFromUrl(url) {
    const value = String(url || "").toLowerCase();

    if (value.includes(".back.") || value.includes("/back/")) return "Back";
    if (
      value.includes(".right.") ||
      value.includes("right_sleeve") ||
      value.includes("sleeve_right") ||
      value.includes("/sleeveright/")
    ) {
      return "Right";
    }
    if (
      value.includes(".left.") ||
      value.includes("left_sleeve") ||
      value.includes("sleeve_left") ||
      value.includes("/sleeveleft/")
    ) {
      return "Left";
    }

    return "Front";
  }

  function uniqueList(values) {
    return Array.from(
      new Set(
        (values || [])
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      )
    );
  }

  function uniqueImageList(values) {
    const seen = new Set();

    return (values || [])
      .map((value) => normalizeImage(value))
      .filter((value) => {
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
      });
  }

  function getSidePublisherArtworkCandidates(design, side) {
    const designId = extractDesignId(design);
    if (!designId) return [];

    const normalizedSide = normalizeSideName(side);
    const base = `https://cdn.inksoft.com/images/publishers/2959/designs/${designId}`;

    if (normalizedSide === "Back") {
      return uniqueImageList([
        `${base}.back.480.png`,
        `${base}.Back.480.png`,
      ]);
    }

    if (normalizedSide === "Left") {
      return uniqueImageList([
        `${base}.left.480.png`,
        `${base}.sleeveleft.480.png`,
        `${base}.leftsleeve.480.png`,
        `${base}.left-sleeve.480.png`,
        `${base}.sleeve-left.480.png`,
      ]);
    }

    if (normalizedSide === "Right") {
      return uniqueImageList([
        `${base}.right.480.png`,
        `${base}.sleeveright.480.png`,
        `${base}.rightsleeve.480.png`,
        `${base}.right-sleeve.480.png`,
        `${base}.sleeve-right.480.png`,
      ]);
    }

    return uniqueImageList([
      `${base}.front.480.png`,
      `${base}.Front.480.png`,
    ]);
  }

  function getSidePublisherArtwork(design, side) {
    return getSidePublisherArtworkCandidates(design, side)[0] || "";
  }

  function allowSideArtworkUrl(url, side) {
    const normalizedSide = normalizeSideName(side);
    const value = normalizeImage(url);

    if (!value) return "";

    /*
      v100:
      Restore Back artwork overlays while still blocking bad rendered garment
      fallbacks. Real InkSoft transparent design PNGs are allowed on enabled
      sides, including Back/Left/Right:
        /images/publishers/.../designs/{designId}.back.480.png
    */
    if (isInksoftTransparentArtworkUrl(value)) return value;

    if (isNonFrontPublisherArtworkUrl(value, normalizedSide)) {
      return "";
    }

    return value;
  }

  function getAdminArtworkUrlForSide(design, side) {
    if (!design || typeof design !== "object") return "";

    const normalizedSide = normalizeSideName(side);
    const sideKey = normalizedSide.toLowerCase();

    const direct = normalizeImage(
      design[`${sideKey}ArtworkUrl`] ||
        design[`${sideKey}_artwork_url`] ||
        design[`${sideKey}ArtworkImageUrl`] ||
        ""
    );

    const rows = [];
    if (Array.isArray(design.artworkViews)) rows.push(...design.artworkViews);
    if (Array.isArray(design.mockups)) rows.push(...design.mockups);
    if (Array.isArray(design.apparelReferences)) rows.push(...design.apparelReferences);
    if (Array.isArray(design.allowedApparel)) rows.push(...design.allowedApparel);

    const match = rows.find((row) => {
      if (!row || typeof row !== "object") return false;
      return normalizeSideName(row.side || row.view || row.placementSide || row.label) === normalizedSide;
    });

    const rowArtwork = normalizeImage(
      (match && (
        match.artworkUrl ||
        match.artworkImageUrl ||
        match.designArtworkUrl ||
        match.sourceArtworkUrl ||
        match.transparentArtworkUrl ||
        match.transparentImageUrl
      )) || ""
    );

    const publisherSide = getSidePublisherArtwork(design, normalizedSide);

    const safeDirect = allowSideArtworkUrl(direct, normalizedSide);
    const safeRowArtwork = allowSideArtworkUrl(rowArtwork, normalizedSide);
    const safePublisherSide = allowSideArtworkUrl(publisherSide, normalizedSide);

    if (isInksoftTransparentArtworkUrl(safeDirect)) return safeDirect;
    if (isInksoftTransparentArtworkUrl(safeRowArtwork)) return safeRowArtwork;
    if (isInksoftTransparentArtworkUrl(safePublisherSide)) return safePublisherSide;

    if (safeDirect) return safeDirect;
    if (safeRowArtwork) return safeRowArtwork;
    if (safePublisherSide) return safePublisherSide;

    return "";
  }

  function getDesignArtworkViews(design) {
    const views = [];
    const seen = new Set();
    const matrixArtwork = getMatrixArtworkForDesign(design);

    const addView = (side, artworkUrl, label, fallbackUrl = "", options = {}) => {
      const normalizedSide = normalizeSideName(side);
      const allowPublisherFallback = Boolean(options.allowPublisherFallback);
      const forceView = Boolean(options.forceView);
      const optionalArtworkSideOverride = Boolean(options.optionalArtworkSide);
      /*
        v97:
        Publisher side URLs are guessed URLs. They are useful for the Front
        transparent artwork, but they can accidentally revive old Back/Left/Right
        artwork renders from InkSoft. For non-front views, use only explicit
        Admin/API artwork URLs.
      */
      const sidePublisherCandidates = allowPublisherFallback && normalizedSide === "Front"
        ? getSidePublisherArtworkCandidates(design, normalizedSide)
        : [];
      const fallbackCandidates = Array.isArray(fallbackUrl) ? fallbackUrl : [fallbackUrl];
      const explicitArtworkUrl = normalizeImage(artworkUrl);

      const orderedCandidates = isInksoftTransparentArtworkUrl(explicitArtworkUrl)
        ? [explicitArtworkUrl, ...sidePublisherCandidates, ...fallbackCandidates]
        : [...sidePublisherCandidates, explicitArtworkUrl, ...fallbackCandidates];

      const candidates = uniqueImageList(
        orderedCandidates
          .map((candidate) => allowSideArtworkUrl(candidate, normalizedSide))
          .filter(Boolean)
      );
      const url = candidates[0] || "";
      const fallbackUrls = candidates.slice(1);

      if ((!url && !forceView) || seen.has(normalizedSide)) return;

      seen.add(normalizedSide);
      views.push({
        side: normalizedSide,
        artworkUrl: url,
        fallbackUrl: fallbackUrls[0] || "",
        fallbackUrls,
        label: label || normalizedSide,
        optionalArtworkSide: ["Left", "Right"].includes(normalizedSide) && (optionalArtworkSideOverride || !explicitArtworkUrl),
      });
    };

    const adminEnabledSides = getAdminEnabledSidesForDesign(design);

    if (adminEnabledSides.length) {
      adminEnabledSides.forEach((side) => {
        const normalizedSide = normalizeSideName(side);
        const explicitArtwork = getAdminArtworkUrlForSide(design, normalizedSide);
        const sideArtworkFallbacks = normalizedSide === "Front"
          ? []
          : getSidePublisherArtworkCandidates(design, normalizedSide);

        addView(
          normalizedSide,
          explicitArtwork || sideArtworkFallbacks[0] || "",
          normalizedSide,
          uniqueImageList([explicitArtwork, ...sideArtworkFallbacks]),
          {
            allowPublisherFallback: normalizedSide === "Front",
            forceView: true,
            optionalArtworkSide: ["Left", "Right"].includes(normalizedSide)
          }
        );
      });
    } else if (matrixArtwork && matrixArtwork.sides) {
      ["Front", "Back", "Left", "Right"].forEach((side) => {
        const sideRecord = matrixArtwork.sides[side];
        if (!sideRecord) return;
        if (!sideRecord.displaySide) return;
        if (sideRecord.showInVisualizer === false) return;

        const normalizedSide = normalizeSideName(sideRecord.side || side);
        const explicitArtwork =
          sideRecord.artworkUrlOverride ||
          getAdminArtworkUrlForSide(design, normalizedSide) ||
          "";

        addView(
          normalizedSide,
          explicitArtwork,
          sideRecord.placementLabel || sideRecord.placementPreset || normalizedSide,
          explicitArtwork,
          { allowPublisherFallback: normalizedSide === "Front", forceView: true }
        );
      });
    }

    if (!views.length) {
      const frontArtworkCandidates = uniqueImageList([
        getSidePublisherArtwork(design, "Front"),
        getPublisherArtwork(design),
        getThumbnailArtwork(design),
        normalizeImage(design.frontArtworkUrl),
        normalizeImage(design.front_artwork_url),
        normalizeImage(design.previewImageUrl),
        normalizeImage(design.imageUrl),
      ]);

      addView(
        "Front",
        frontArtworkCandidates[0] || "",
        "Front",
        frontArtworkCandidates.slice(1),
        { allowPublisherFallback: true }
      );
    }

    const order = { Front: 1, Back: 2, Left: 3, Right: 4 };

    return views.sort((a, b) => (order[a.side] || 99) - (order[b.side] || 99));
  }


  function getMockupFallbackUrlsForSide(apparel, side) {
    /*
      v98:
      Side mockup resolver. Only the selected, normalized apparel record may
      provide garment mockup images. Every URL is screened so old rendered/JIT
      design images cannot come back as image fallbacks.
    */
    if (!apparel) return [];

    const normalizedSide = normalizeSideName(side);
    const safeCandidates = function () {
      return mockupUrlCandidates.apply(null, Array.prototype.slice.call(arguments)
        .map(safeGarmentMockupUrl)
        .filter(Boolean)
      );
    };

    if (normalizedSide === "Back") {
      return safeCandidates(
        apparel.back,
        apparel.backImageUrl,
        apparel.backGarmentImageUrl,
        apparel.backUrl
      );
    }

    if (normalizedSide === "Left") {
      return safeCandidates(
        apparel.left,
        apparel.leftImageUrl,
        apparel.leftGarmentImageUrl,
        apparel.leftSleeveImageUrl,
        apparel.leftUrl
      );
    }

    if (normalizedSide === "Right") {
      return safeCandidates(
        apparel.right,
        apparel.rightImageUrl,
        apparel.rightGarmentImageUrl,
        apparel.rightSleeveImageUrl,
        apparel.rightUrl
      );
    }

    return safeCandidates(
      apparel.front,
      apparel.frontImageUrl,
      apparel.frontGarmentImageUrl,
      apparel.frontUrl,
      apparel.apparelImageUrl
    );
  }

  function normalizeSideMappingSide(value, fallback) {
    const text = String(value || "").trim().toLowerCase();
    if (text === "back") return "Back";
    if (text === "left") return "Left";
    if (text === "right") return "Right";
    if (text === "front") return "Front";
    return fallback || "Front";
  }

  function getSideMappingEntry(apparel, displaySide) {
    const side = normalizeSideMappingSide(displaySide, "Front");
    const mapping = apparel && apparel.sideMapping && typeof apparel.sideMapping === "object"
      ? apparel.sideMapping
      : {};
    const row = mapping[side] || mapping[side.toLowerCase()] || {};
    return {
      imageSource: normalizeSideMappingSide(row.imageSource || row.source || row.imageSide, side),
      displayLabel: String(row.displayLabel || row.label || side).trim() || side
    };
  }

  function getMockupUrlForSide(apparel, side) {
    const mapping = getSideMappingEntry(apparel, side);
    return getMockupFallbackUrlsForSide(apparel, mapping.imageSource)[0] || "";
  }

  function getDefaultUnmappedApparelRecord() {
    const library = getCombinedApparelLibrary();

    return (
      library.find((item) =>
        String(item.productId) === "1010290" &&
        String(item.styleId) === "1106313" &&
        (String(item.colorName || "").toLowerCase() === "white" ||
          String(item.colorFamily || "").toLowerCase() === "white")
      ) ||
      library.find((item) =>
        String(item.productId) === "1010290" &&
        String(item.styleId) === "1106313"
      ) ||
      library.find((item) =>
        item.category === "Short Sleeve T-Shirt" && item.colorFamily === "White"
      ) ||
      DEFAULT_APPAREL
    );
  }

  function normalizeFilterText(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_\-]+/g, " ");
  }

  function recordMatchesAllowedColor(record, colors) {
    if (!Array.isArray(colors) || !colors.length) return true;

    const allowedColors = colors
      .map((color) => normalizeFilterText(color))
      .filter(Boolean);

    if (!allowedColors.length) return true;

    const recordColorNames = [
      record.colorName,
      record.colorFamily,
      record.curatedColorFamily,
      record.tone,
    ]
      .map((value) => normalizeFilterText(value))
      .filter(Boolean);

    return allowedColors.some((allowed) => recordColorNames.includes(allowed));
  }

  function buildAllowedApparelRules(matrixArtwork) {
    const rules = [];

    Object.entries(matrixArtwork?.allowedApparel || {}).forEach(([category, rule]) => {
      const productIds = Array.isArray(rule?.productIds)
        ? rule.productIds.map((value) => String(value)).filter(Boolean)
        : [];

      const colors = Array.isArray(rule?.colors)
        ? rule.colors.map((value) => String(value)).filter(Boolean)
        : [];

      rules.push({
        category,
        productIds,
        colors,
      });
    });

    const defaultApparel = matrixArtwork?.defaultApparel || {};
    if (defaultApparel.productId || defaultApparel.color || defaultApparel.apparelType) {
      const category = defaultApparel.apparelType || "";
      let rule = rules.find((item) => item.category === category);

      if (!rule) {
        rule = {
          category,
          productIds: [],
          colors: [],
        };
        rules.push(rule);
      }

      if (defaultApparel.productId && !rule.productIds.includes(String(defaultApparel.productId))) {
        rule.productIds.push(String(defaultApparel.productId));
      }

      if (defaultApparel.color && !rule.colors.includes(String(defaultApparel.color))) {
        rule.colors.push(String(defaultApparel.color));
      }
    }

    return rules;
  }

  function recordMatchesAllowedApparelRule(record, rule) {
    if (!record || !rule) return false;

    const categoryMatches = !rule.category || record.category === rule.category;
    const productMatches =
      !Array.isArray(rule.productIds) ||
      !rule.productIds.length ||
      rule.productIds.includes(String(record.productId));

    return (
      categoryMatches &&
      productMatches &&
      recordMatchesAllowedColor(record, rule.colors)
    );
  }


  function isGhostRemovedApparelRecord(record) {
    if (!record) return false;

    const productId = String(record.productId || "").trim();
    const styleId = String(record.styleId || "").trim();
    const mfgSku = String(
      record.mfgSku ||
        record.manufacturerSku ||
        record.productSku ||
        record.sku ||
        ""
    )
      .trim()
      .toLowerCase();
    const productName = String(record.productName || record.name || "")
      .trim()
      .toLowerCase();
    const colorName = String(record.colorName || record.colorFamily || record.color || "")
      .trim()
      .toLowerCase();

    /*
      v92 temporary public-side safety block.

      Removes the old Gildan 18500 White ghost row that is still saved inside
      design-specific metadata even after it was removed from the Admin
      Approved Apparel Library.

      Known stale row:
      Product ID 1010285 / Style ID 1106147 / Gildan 18500 / White
    */
    if (productId === "1010285" && styleId === "1106147") return true;

    if (
      colorName === "white" &&
      (
        mfgSku === "18500" ||
        mfgSku.includes("gildan 18500") ||
        productName.includes("gildan 18500") ||
        (
          productId === "1010285" &&
          productName.includes("heavy blend") &&
          productName.includes("hooded sweatshirt")
        )
      )
    ) {
      return true;
    }

    return false;
  }

  function pruneGhostApparelRecords(records) {
    return (Array.isArray(records) ? records : []).filter((record) => {
      return record && !isGhostRemovedApparelRecord(record);
    });
  }

  function getAvailableApparelRecords(design) {
    const library = pruneGhostApparelRecords(getCombinedApparelLibrary());
    const matrixArtwork = getMatrixArtworkForDesign(design);

    const adminRecords = uniqueApparelRecords(
      pruneGhostApparelRecords(getAdminApparelRecordsForDesign(design))
    ).filter((record) => {
      return record &&
        String(record.productId || "").trim() &&
        String(record.styleId || "").trim();
    });

    /*
      v100:
      Admin-approved apparel is authoritative.

      If a design already has Admin-saved apparel rows, do not merge Matrix
      allowed/default apparel into the public visualizer. This prevents stale
      Matrix entries, such as old Gildan 5000 rows for Lawrence, from returning
      after the Admin selection has moved to A4.
    */
    if (adminRecords.length) {
      return adminRecords;
    }

    if (!matrixArtwork) {
      const defaultRecord = pruneGhostApparelRecords([
        getDefaultUnmappedApparelRecord()
      ])[0];

      return defaultRecord ? [defaultRecord] : pruneGhostApparelRecords([DEFAULT_APPAREL]);
    }

    const allowedRules = buildAllowedApparelRules(matrixArtwork);

    if (allowedRules.length) {
      const filtered = library.filter((record) =>
        allowedRules.some((rule) => recordMatchesAllowedApparelRule(record, rule))
      );

      return pruneGhostApparelRecords(filtered);
    }

    const explicitProductIds = [
      design.defaultProductId,
      design.default_product_id,
      design.short_sleeve_tshirt_product_id,
      design.long_sleeve_tshirt_product_id,
      design.pullover_sweatshirt_product_id,
      design.crewneck_sweatshirt_product_id,
      design.zipped_sweatshirt_product_id,
      design.polo_product_id,
      design.shorts_product_id,
      design.pants_product_id,
      design.tote_bag_product_id,
      design.cinch_bag_product_id,
    ]
      .filter(Boolean)
      .map((value) => String(value));

    const nestedProductIds = []
      .concat(Array.isArray(design.allowedProductIds) ? design.allowedProductIds : [])
      .concat(Array.isArray(design.allowed_product_ids) ? design.allowed_product_ids : []);

    const matrixProductIds = [];

    if (matrixArtwork.allowedApparel) {
      Object.values(matrixArtwork.allowedApparel).forEach((item) => {
        (item.productIds || []).forEach((productId) => {
          if (productId) matrixProductIds.push(String(productId));
        });
      });
    }

    if (matrixArtwork?.defaultApparel?.productId) {
      matrixProductIds.push(String(matrixArtwork.defaultApparel.productId));
    }

    const allowedIds = new Set(
      explicitProductIds
        .concat(nestedProductIds)
        .concat(matrixProductIds)
        .filter(Boolean)
        .map(String)
    );

    if (!allowedIds.size) {
      return pruneGhostApparelRecords(library);
    }

    const filteredById = library.filter((item) => allowedIds.has(String(item.productId)));

    return pruneGhostApparelRecords(filteredById);
  }

  function getDefaultApparel(design) {
    const adminRecords = pruneGhostApparelRecords(
      getAdminApparelRecordsForDesign(design)
    );

    if (adminRecords.length) return adminRecords[0];

    const matrixArtwork = getMatrixArtworkForDesign(design);

    if (!matrixArtwork) {
      return pruneGhostApparelRecords([getDefaultUnmappedApparelRecord()])[0] || null;
    }

    const records = pruneGhostApparelRecords(getAvailableApparelRecords(design));

    const defaultProductId = String(
      design.defaultProductId || design.default_product_id || ""
    );

    const defaultStyleId = String(
      design.defaultStyleId || design.default_style_id || ""
    );

    if (defaultProductId && defaultStyleId) {
      const direct = records.find(
        (item) =>
          String(item.productId) === defaultProductId &&
          String(item.styleId) === defaultStyleId
      );

      if (direct) return direct;
    }

    return (
      records.find((item) => item.defaultStyle) ||
      records[0] ||
      null
    );
  }


  function apparelProductGroupKey(record) {
    if (!record) return "";

    const productId = String(record.productId || "").trim();
    const sku = String(
      record.mfgSku ||
      record.manufacturerSku ||
      record.productSku ||
      record.sku ||
      ""
    ).trim();

    return [productId, sku].filter(Boolean).join("|") || String(record.key || "");
  }

  function apparelProductGroupLabel(record) {
    if (!record) return "Apparel";

    return String(
      record.mfgSku ||
      record.manufacturerSku ||
      record.productSku ||
      record.sku ||
      record.productId ||
      "Apparel"
    ).trim();
  }

  function exactApprovedColorLabel(record) {
    return String(record && (record.colorName || record.selectedColor || record.defaultColor) || "").trim();
  }

  function uniqueProductColorRecords(records) {
    const seen = new Set();

    return (records || []).filter((record) => {
      if (!record) return false;

      const key = [
        apparelProductGroupKey(record),
        String(record.styleId || "").trim(),
        exactApprovedColorLabel(record).toLowerCase(),
      ].join("|");

      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function groupApparelByCategory(records) {
    const groups = [];

    uniqueProductColorRecords(records).forEach((record) => {
      const groupKey = apparelProductGroupKey(record);
      if (!groupKey) return;

      let group = groups.find((item) => item.key === groupKey);

      if (!group) {
        group = {
          key: groupKey,
          category: groupKey,
          label: apparelProductGroupLabel(record),
          records: [],
        };
        groups.push(group);
      }

      group.records.push(record);
    });

    groups.forEach((group) => {
      group.records.sort((a, b) =>
        exactApprovedColorLabel(a).localeCompare(
          exactApprovedColorLabel(b),
          undefined,
          { sensitivity: "base", numeric: true }
        )
      );
    });

    return groups;
  }

  function getFirstRecordForCategory(records, category, currentRecord) {
    if (currentRecord && apparelProductGroupKey(currentRecord) === category) {
      return currentRecord;
    }

    const matches = uniqueProductColorRecords(records).filter(
      (item) => apparelProductGroupKey(item) === category
    );

    return (
      matches.find((item) => item.defaultStyle) ||
      matches[0] ||
      currentRecord
    );
  }

  function buildMockup(design, apparelRecord = DEFAULT_APPAREL, cardMode = false) {
    if (!apparelRecord) {
      return `<div class="di-product-mockup__placeholder">No approved apparel reference</div>`;
    }

    const artworkViews = getDesignArtworkViews(design);
    const viewsToRender = cardMode ? artworkViews.slice(0, 1) : artworkViews;

    const gridColumns =
      viewsToRender.length >= 4
        ? "repeat(2, minmax(0, 1fr))"
        : `repeat(${Math.max(1, viewsToRender.length)}, minmax(0, 1fr))`;

    const sideCards = viewsToRender
      .map((view) => {
        const matrixSide = getMatrixSideForDesign(design, view.side);

        /*
          v96:
          Do NOT use matrixSide.mockupUrlOverride.
          That field can revive old manually defined renderings.
          The selected Admin apparel record is the only mockup source.
        */
        const sideMapping = getSideMappingEntry(apparelRecord, view.side);
        const mockupUrl = getMockupUrlForSide(apparelRecord, view.side);

        const viewMode = cardMode ? "Grid" : "Modal";
        const artworkPlacementStyle = getMatrixPlacementStyle(
          design,
          view.side,
          viewMode,
          apparelRecord
        );

        const apparelPositionStyle = getMatrixApparelPositionStyle(
          design,
          view.side,
          viewMode,
          apparelRecord
        );

        const sideHasMockup = Boolean(mockupUrl);

        return `
          <div
            class="di-product-mockup__view"
            data-view-side="${escapeHtml(view.side)}"
            data-optional-artwork-side="${view.optionalArtworkSide ? "true" : "false"}"
            data-has-mockup="${sideHasMockup ? "true" : "false"}"
          >
            <div
              class="di-product-mockup"
              data-product-id="${escapeHtml(apparelRecord.productId)}"
              data-style-id="${escapeHtml(apparelRecord.styleId)}"
              data-color="${escapeHtml(apparelRecord.colorName)}"
              data-side="${escapeHtml(view.side)}"
              data-image-source-side="${escapeHtml(sideMapping.imageSource)}"
              data-display-side-label="${escapeHtml(sideMapping.displayLabel)}"
              data-apparel-category="${escapeHtml(apparelRecord.category)}"
              data-apparel-type="${escapeHtml(apparelRecord.type)}"
              data-mfg-sku="${escapeHtml(apparelRecord.mfgSku)}"
              data-admin-sku-placement-keys="${escapeHtml(getPlacementSkuCandidateKeys(apparelRecord).join(" || "))}"
              data-admin-sku-placement-applied="${getAdminSkuPlacementEntry(design, apparelRecord) ? "Yes" : "No"}"
              data-admin-mockup-applied="${getAdminApparelRecordForDesign(design)?.adminConfigured ? "Yes" : "No"}"
              data-matrix-applied="${getMatrixArtworkForDesign(design) ? "Yes" : "No"}"
              data-matrix-default-product-id="${escapeHtml(getMatrixArtworkForDesign(design)?.defaultApparel?.productId || "")}"
              data-matrix-default-style-id="${escapeHtml(getMatrixArtworkForDesign(design)?.defaultApparel?.styleId || "")}"
              data-strict-mockup-source="admin-selected-apparel-only"
              data-responsive-locked-canvas="true"
              data-view-mode="${cardMode ? "grid" : "modal"}"
            >
              ${cardMode ? `<div class="di-product-mockup__grid-fixed-stage" aria-hidden="true">` : ""}

              ${
                mockupUrl
                  ? `
                    <img
                      class="di-product-mockup__shirt"
                      src="${escapeHtml(mockupUrl)}"
                      ${apparelPositionStyle ? `style="${escapeHtml(apparelPositionStyle)}"` : ""}
                      data-matrix-apparel-position-style="${escapeHtml(apparelPositionStyle)}"
                      data-fallback-srcs=""
                      data-strict-no-old-render-fallback="true"
                      alt="${escapeHtml(apparelRecord.productName)} ${escapeHtml(sideMapping.displayLabel)} mockup"
                      loading="lazy"
                      onerror="
                        this.style.display='none';

                        const viewWrap = this.closest('.di-product-mockup__view');
                        const mockup = this.closest('.di-product-mockup');
                        const side = mockup ? mockup.dataset.side : '';

                        if (viewWrap && side && side !== 'Front') {
                          viewWrap.style.display = 'none';
                        }
                      "
                    >
                  `
                  : `
                    <div class="di-product-mockup__placeholder">
                      Missing ${escapeHtml(sideMapping.displayLabel)} mockup
                    </div>
                  `
              }

              ${
                view.artworkUrl
                  ? `
                    <img
                      class="di-product-mockup__artwork"
                      src="${escapeHtml(view.artworkUrl)}"
                      ${artworkPlacementStyle ? `style="${escapeHtml(artworkPlacementStyle)}"` : ""}
                      data-matrix-placement-preset="${escapeHtml(matrixSide?.placementPreset || matrixSide?.placementLabel || "")}"
                      data-matrix-placement-style="${escapeHtml(artworkPlacementStyle)}"
                      alt="${escapeHtml(design.title)} ${escapeHtml(view.side)} artwork"
                      loading="lazy"
                      data-optional-artwork-side="${view.optionalArtworkSide ? "true" : "false"}"
                      data-fallback-srcs="${escapeHtml(
                        uniqueImageList([
                          ...(Array.isArray(view.fallbackUrls) ? view.fallbackUrls : []),
                          view.fallbackUrl,
                          ...(view.side === "Front"
                            ? [
                                getThumbnailArtwork(design),
                                normalizeImage(design.previewImageUrl),
                                normalizeImage(design.imageUrl),
                              ]
                            : []),
                        ]).join("||")
                      )}"
                      onerror="
                        const fallbackList = (this.dataset.fallbackSrcs || '').split('||').filter(Boolean);
                        const currentSrc = this.getAttribute('src') || '';
                        const nextSrc = fallbackList.find((src) => src && src !== currentSrc && src !== this.src);

                        if (nextSrc) {
                          this.dataset.fallbackSrcs = fallbackList.filter((src) => src !== nextSrc).join('||');
                          this.src = nextSrc;
                        } else {
                          if (this.dataset.optionalArtworkSide === 'true') {
                            this.style.display='none';
                          } else {
                            this.style.display='none';
                          }
                        }
                      "
                    >
                  `
                  : ""
              }

              ${cardMode ? `</div>` : ""}
            </div>

            ${cardMode ? "" : `<div class="di-product-mockup__caption">${escapeHtml(sideMapping.displayLabel)}</div>`}
          </div>
        `;
      })
      .join("");

    return `
      <div
        class="di-product-mockup-grid"
        data-side-count="${escapeHtml(viewsToRender.length)}"
        style="display:grid;grid-template-columns:${gridColumns};gap:20px;align-items:start;width:100%;"
      >
        ${sideCards}
      </div>
    `;
  }

  function repositionHomeSections() {
    const howSection = els.howItWorks?.closest(".di-section");
    const categorySection = els.primaryCategories?.closest(".di-section");

    if (!howSection || !categorySection || howSection === categorySection) return;

    // Place How It Works immediately before Browse by Category.
    categorySection.parentNode?.insertBefore(howSection, categorySection);
  }

  const IDENTIFIED_DESIGN_CATEGORIES = [
    "PE Uniforms",
    "Spirit Wear",
    "Athletics",
    "Clubs & Organizations",
    "Grad Apparel",
    "Events",
    "Seasonal",
    "Teachers & Staff",
    "Corporate",
  ];

  /*
     Shopify category pages use the native /pages/<handle> route.

     Important:
     The backend collections endpoint may still return URLs such as
     /design-inspiration/pe-uniforms. Those are not native Shopify Page
     routes and will return a 404 unless a separate app route exists.

     This map is therefore the source of truth for Browse by Category links.
  */
  const CATEGORY_PAGE_URLS = {
    "PE Uniforms": "/pages/design-inspiration-pe-uniforms",
    "Spirit Wear": "/pages/design-inspiration-spirit-wear",
    "Athletics": "/pages/design-inspiration-athletics",
    "Clubs & Organizations": "/pages/design-inspiration-clubs-organizations",
    "Grad Apparel": "/pages/design-inspiration-grad-apparel",
    "Events": "/pages/design-inspiration-events",
    "Seasonal": "/pages/design-inspiration-seasonal",
    "Teachers & Staff": "/pages/design-inspiration-teachers-staff",
    "Corporate": "/pages/design-inspiration-corporate",
  };

  function getCategoryPageUrl(value) {
    const title = normalizeDesignCategoryName(value);

    return (
      CATEGORY_PAGE_URLS[title] ||
      `/pages/design-inspiration?segment=${encodeURIComponent(title)}`
    );
  }

  function normalizeDesignCategoryName(value) {
    const raw = String(value || "").trim();
    const key = raw.toLowerCase().replace(/\s+/g, " ");

    const aliases = {
      "club & organization": "Clubs & Organizations",
      "clubs & organization": "Clubs & Organizations",
      "clubs and organizations": "Clubs & Organizations",
      "club and organizations": "Clubs & Organizations",
      "graduation": "Grad Apparel",
      "graduate apparel": "Grad Apparel",
      "graduation apparel": "Grad Apparel",
      "teacher & staff": "Teachers & Staff",
      "teachers and staff": "Teachers & Staff",
      "teacher and staff": "Teachers & Staff",
      "corporate apparel": "Corporate",
      "seasonal apparel": "Seasonal",
    };

    return aliases[key] || raw;
  }

  function ensureCategoryRowStyles() {
    if (document.getElementById("di-category-row-overrides")) return;

    const style = document.createElement("style");
    style.id = "di-category-row-overrides";
    style.textContent = `
      #di-primary-categories.di-category-grid {
        display: grid !important;
        grid-auto-flow: column !important;
        grid-auto-columns: minmax(185px, 1fr) !important;
        grid-template-columns: none !important;
        gap: 12px !important;
        width: 100% !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        padding: 2px 2px 12px !important;
        scroll-snap-type: x proximity;
        -webkit-overflow-scrolling: touch;
      }

      #di-primary-categories .di-category-card {
        min-width: 185px !important;
        height: 100% !important;
        scroll-snap-align: start;
      }

      #di-primary-categories {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      #di-primary-categories::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
      }

      .di-category-section__head {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 20px;
      }

      .di-category-scroll-controls {
        display: flex;
        align-items: center;
        gap: 6px;
        flex: 0 0 auto;
      }

      .di-category-scroll-button {
        width: 32px;
        height: 32px;
        padding: 0;
        border: 1px solid #dbe3ef;
        border-radius: 7px;
        background: #ffffff;
        color: #123f7c;
        font-size: 20px;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }

      .di-category-scroll-button:hover:not(:disabled) {
        background: #f2f6fc;
        border-color: #123f7c;
      }

      .di-category-scroll-button:disabled {
        opacity: 0.35;
        cursor: default;
      }

      @media (max-width: 700px) {
        #di-primary-categories.di-category-grid {
          grid-auto-columns: minmax(170px, 78vw) !important;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function getCategoryScrollDistance() {
    if (!els.primaryCategories) return 240;

    const firstCard = els.primaryCategories.querySelector(".di-category-card");
    if (firstCard) {
      const rowStyles = window.getComputedStyle(els.primaryCategories);
      const gap = parseFloat(rowStyles.columnGap || rowStyles.gap || "12") || 12;
      return firstCard.getBoundingClientRect().width + gap;
    }

    return Math.max(240, els.primaryCategories.clientWidth * 0.8);
  }

  function updateCategoryScrollButtons() {
    const scroller = els.primaryCategories;
    if (!scroller) return;

    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const scrollingRequired = maxScrollLeft > 2;
    const atStart = scroller.scrollLeft <= 2;
    const atEnd = scroller.scrollLeft >= maxScrollLeft - 2;

    if (els.categoryPrev) {
      els.categoryPrev.disabled = !scrollingRequired || atStart;
    }

    if (els.categoryNext) {
      els.categoryNext.disabled = !scrollingRequired || atEnd;
    }

    if (els.categoryControls) {
      els.categoryControls.hidden = !scrollingRequired;
    }
  }

  function scrollCategories(direction) {
    if (!els.primaryCategories) return;

    els.primaryCategories.scrollBy({
      left: getCategoryScrollDistance() * direction,
      behavior: "smooth",
    });
  }

  function buildAllCategoryCards(designs, configuredCategories) {
    const categoryMap = new Map();

    // Always include the approved Unisource Apparel Originals taxonomy,
    // including categories that currently contain zero published designs.
    IDENTIFIED_DESIGN_CATEGORIES.forEach((title, index) => {
      categoryMap.set(title.toLowerCase(), {
        label: title,
        name: title,
        segment: title,
        designCount: 0,
        taxonomyIndex: index,
        url: getCategoryPageUrl(title),
      });
    });

    // Preserve any endpoint-provided category metadata while retaining
    // the approved category label and display order above.
    (Array.isArray(configuredCategories) ? configuredCategories : []).forEach((category) => {
      const sourceTitle = String(
        category?.label || category?.name || category?.segment || ""
      ).trim();
      const title = normalizeDesignCategoryName(sourceTitle);

      if (!title) return;

      const key = title.toLowerCase();
      const existing = categoryMap.get(key) || {
        label: title,
        name: title,
        segment: title,
        designCount: 0,
        taxonomyIndex: IDENTIFIED_DESIGN_CATEGORIES.length,
      };

      categoryMap.set(key, {
        ...existing,
        ...category,
        label: title,
        name: title,
        segment: title,
        designCount: Number(existing.designCount || 0),
        taxonomyIndex: existing.taxonomyIndex,
        // Do not use category.url from the backend here. The backend may
        // return /design-inspiration/<slug>, which is not a native Shopify
        // Page route and causes a 404. Always use the approved Shopify page.
        url: getCategoryPageUrl(title),
      });
    });

    // Count every visible artwork under its normalized approved category.
    (Array.isArray(designs) ? designs : []).forEach((design) => {
      const sourceTitle = String(design?.segment || "").trim();
      const title = normalizeDesignCategoryName(sourceTitle);
      if (!title) return;

      const key = title.toLowerCase();
      const existing = categoryMap.get(key) || {
        label: title,
        name: title,
        segment: title,
        designCount: 0,
        taxonomyIndex: IDENTIFIED_DESIGN_CATEGORIES.length,
        url: getCategoryPageUrl(title),
      };

      existing.designCount = Number(existing.designCount || 0) + 1;
      categoryMap.set(key, existing);
    });

    return Array.from(categoryMap.values()).sort((a, b) => {
      const orderDifference =
        Number(a.taxonomyIndex ?? IDENTIFIED_DESIGN_CATEGORIES.length) -
        Number(b.taxonomyIndex ?? IDENTIFIED_DESIGN_CATEGORIES.length);

      if (orderDifference !== 0) return orderDifference;

      return String(a.label || "").localeCompare(String(b.label || ""));
    });
  }

  function buildCategoryCard(category) {
    const title =
      category.label ||
      category.name ||
      category.segment ||
      "Design Category";

    const count =
      typeof category.designCount === "number"
        ? `${category.designCount} designs`
        : category.description || "";

    // Resolve the route from the approved category title instead of trusting
    // an endpoint-provided URL.
    const categoryUrl = getCategoryPageUrl(title);

    return `
      <a class="di-category-card" href="${escapeHtml(categoryUrl)}" data-di-category-filter="${escapeHtml(title)}">
        <div class="di-category-card__title">${escapeHtml(title)}</div>
        <div class="di-category-card__count">${escapeHtml(count)}</div>
      </a>
    `;
  }

  function buildDesignCard(design) {
    const tags = designIdeaDisplaySetting("showTags")
      ? (design.tags || [])
          .slice(0, 4)
          .map((tag) => `<span class="di-design-card__tag">${escapeHtml(tag)}</span>`)
          .join("")
      : "";

    return `
      <div
        class="di-design-card"
        data-slug="${escapeHtml(design.slug)}"
        data-admin-mockup-applied="${getAdminApparelRecordForDesign(design)?.adminConfigured ? "Yes" : "No"}"
              data-matrix-applied="${getMatrixArtworkForDesign(design) ? "Yes" : "No"}"
      >
        <div class="di-design-card__image-wrap">
          ${customizationBadgeLabel(design) ? `<span class="di-customizable-badge di-design-card__customizable-badge">${escapeHtml(customizationBadgeLabel(design))}</span>` : ""}
          ${buildMockup(design, getDefaultApparel(design), true)}
        </div>

        <div class="di-design-card__body">
          <div class="di-design-card__segment">
            ${escapeHtml(design.segment || "")}
          </div>

          <div class="di-design-card__title">
            ${escapeHtml(titleWithCustomizableReference(design.title || "", design))}
          </div>
          ${
            designIdeaDisplaySetting("showReferenceLine") &&
            customizableReference(design)
              ? `<div class="di-customizable-reference">Reference: ${escapeHtml(customizableReference(design))}</div>`
              : ""
          }

          <div class="di-design-card__meta">
            ${escapeHtml(design.collection || "")}
            ${design.productGroup ? ` • ${escapeHtml(design.productGroup)}` : ""}
          </div>

          ${tags ? `<div class="di-design-card__tags">${tags}</div>` : ""}
        </div>

        <div class="di-design-card__actions">
          <button
            class="di-design-card__button"
            type="button"
            data-open-design="${escapeHtml(design.slug)}"
          >
            ${isDesignCustomizable(design) ? "View & Customize" : "View Full Image"}
          </button>
        </div>
      </div>
    `;
  }

  function renderHowItWorks(items) {
    if (!els.howItWorks || !Array.isArray(items)) return;

    els.howItWorks.innerHTML = items
      .map((item, index) => {
        return `
          <div class="di-how-card">
            <div class="di-how-card__number">${index + 1}</div>
            <div class="di-how-card__title">${escapeHtml(item.title)}</div>
            <div class="di-how-card__text">${escapeHtml(item.description)}</div>
          </div>
        `;
      })
      .join("");
  }


  const sectionState = {
    allDesigns: {
      items: [],
      page: 1,
      grid: () => els.allDesigns,
      pager: () => els.allDesignsPagination,
    },
  };

  function getPageCount(totalItems) {
    return Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  }

  function getVisiblePageNumbers(currentPage, pageCount) {
    if (pageCount <= 7) {
      return Array.from({ length: pageCount }, (_, index) => index + 1);
    }

    const pages = [1];

    if (currentPage > 4) {
      pages.push("ellipsis-start");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(pageCount - 1, currentPage + 1);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < pageCount - 3) {
      pages.push("ellipsis-end");
    }

    pages.push(pageCount);

    return pages;
  }

  function buildPagerButton({
    section,
    page,
    label,
    active = false,
    disabled = false,
    nav = false,
    action = "",
  }) {
    const actionAttribute = action
      ? ` data-di-page-action="${escapeHtml(action)}"`
      : "";

    return `
      <button
        type="button"
        class="di-page-btn${active ? " is-active" : ""}${nav ? " di-page-btn--nav" : ""}"
        data-di-page-section="${escapeHtml(section)}"
        data-di-page="${escapeHtml(page)}"${actionAttribute}
        ${active ? 'aria-current="page"' : ""}
        ${disabled ? "disabled" : ""}
      >
        ${escapeHtml(label)}
      </button>
    `;
  }

  function renderSectionPager(sectionName) {
    const state = sectionState[sectionName];
    if (!state) return;

    const pager = state.pager();
    if (!pager) return;

    const totalItems = state.items.length;
    const pageCount = getPageCount(totalItems);

    if (totalItems <= PAGE_SIZE) {
      pager.hidden = true;
      pager.innerHTML = "";
      return;
    }

    const pageNumbers = getVisiblePageNumbers(state.page, pageCount);

    const numberMarkup = pageNumbers
      .map((page) => {
        if (typeof page === "string") {
          return `<span class="di-pagination__ellipsis" aria-hidden="true">…</span>`;
        }

        return buildPagerButton({
          section: sectionName,
          page,
          label: String(page),
          active: page === state.page,
        });
      })
      .join("");

    const firstItem = (state.page - 1) * PAGE_SIZE + 1;
    const lastItem = Math.min(state.page * PAGE_SIZE, totalItems);

    pager.innerHTML = `
      <div class="di-pagination__summary">
        Showing ${firstItem}–${lastItem} of ${totalItems}
      </div>

      <div class="di-pagination__controls" role="navigation" aria-label="Design pages">
        ${buildPagerButton({
          section: sectionName,
          page: Math.max(1, state.page - 1),
          label: "Previous",
          disabled: state.page === 1,
          nav: true,
          action: "prev",
        })}

        <div class="di-pagination__numbers">
          ${numberMarkup}
        </div>

        ${buildPagerButton({
          section: sectionName,
          page: Math.min(pageCount, state.page + 1),
          label: "Next",
          disabled: state.page === pageCount,
          nav: true,
          action: "next",
        })}
      </div>
    `;

    pager.hidden = false;
  }

  function getSectionNameFromPagerElement(element) {
    const pager = element?.closest?.(".di-pagination");
    if (!pager) return "";

    if (pager.id === "di-all-designs-pagination") return "allDesigns";

    return "";
  }

  function setSectionPage(sectionName, requestedPage, scrollIntoView = false) {
    const state = sectionState[sectionName];
    if (!state) return false;

    const pageCount = getPageCount(state.items.length);
    const numericPage = Number(requestedPage);
    const nextPage = Math.min(
      pageCount,
      Math.max(1, Number.isFinite(numericPage) ? numericPage : state.page)
    );

    if (nextPage === state.page) return false;

    state.page = nextPage;
    renderSectionPage(sectionName, scrollIntoView);
    return true;
  }

  function getRequestedPageFromPagerButton(pageButton, state) {
    const pageCount = getPageCount(state.items.length);
    const action = pageButton.dataset.diPageAction || "";

    if (action === "next") return Math.min(pageCount, state.page + 1);
    if (action === "prev") return Math.max(1, state.page - 1);

    return Number(pageButton.dataset.diPage);
  }

  function buildDesignCardSafe(item, sectionName, pageNumber, itemIndex) {
    try {
      return buildDesignCard(item);
    } catch (error) {
      const title =
        item?.title ||
        item?.name ||
        item?.publicTitle ||
        item?.artworkTitle ||
        item?.slug ||
        "Design item";

      const designId =
        item?.inksoftDesignId ||
        item?.designId ||
        item?.id ||
        "";

      const errorInfo = {
        sectionName,
        pageNumber,
        itemIndex,
        designId: String(designId || ""),
        title: String(title || ""),
        message: error?.message || String(error),
      };

      window.__UNISOURCE_DI_CARD_RENDER_ERRORS__ =
        window.__UNISOURCE_DI_CARD_RENDER_ERRORS__ || [];
      window.__UNISOURCE_DI_CARD_RENDER_ERRORS__.push(errorInfo);

      console.warn("[Unisource DI] Card render skipped fallback:", errorInfo, error);

      return `
        <article class="di-design-card di-design-card--render-fallback" data-render-fallback="true">
          <div class="di-design-card__image">
            <div class="di-empty-state">Preview unavailable</div>
          </div>
          <div class="di-design-card__body">
            <div class="di-design-card__eyebrow">Needs review</div>
            <h3 class="di-design-card__title">${escapeHtml(title)}</h3>
            <p class="di-design-card__meta">
              This design could not render as a card. Check artwork, apparel, or matrix data.
            </p>
            ${designId ? `<p class="di-design-card__meta">Design ID: ${escapeHtml(designId)}</p>` : ""}
          </div>
        </article>
      `;
    }
  }

  function renderSectionPage(sectionName, scrollIntoView = false) {
    const state = sectionState[sectionName];
    if (!state) return;

    const grid = state.grid();
    if (!grid) return;

    const pageCount = getPageCount(state.items.length);
    const safePage = Math.min(Math.max(1, state.page), pageCount);
    state.page = safePage;

    const start = (safePage - 1) * PAGE_SIZE;
    const items = state.items.slice(start, start + PAGE_SIZE);

    let markup = "";

    try {
      markup = items.length
        ? items
            .map((item, index) =>
              buildDesignCardSafe(item, sectionName, safePage, start + index + 1)
            )
            .join("")
        : `<div class="di-empty-state">No designs are available in this section yet.</div>`;
    } catch (error) {
      window.__UNISOURCE_DI_LAST_PAGE_RENDER_ERROR__ = {
        sectionName,
        page: safePage,
        message: error?.message || String(error),
        time: new Date().toISOString(),
      };

      console.error("[Unisource DI] Page render failed:", window.__UNISOURCE_DI_LAST_PAGE_RENDER_ERROR__, error);

      markup = `
        <div class="di-empty-state">
          This page could not fully render. Please check the browser console for the design record causing the issue.
        </div>
      `;
    }

    grid.innerHTML = markup;

    renderSectionPager(sectionName);

    window.__UNISOURCE_DI_LAST_PAGE_RENDER__ = {
      sectionName,
      page: safePage,
      pageCount,
      startIndex: start + 1,
      itemCountOnPage: items.length,
      totalItems: state.items.length,
      time: new Date().toISOString(),
    };

    if (scrollIntoView) {
      const section = grid.closest(".di-section");
      if (section) {
        section.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }

  function setSectionItems(sectionName, items) {
    const state = sectionState[sectionName];
    if (!state) return;

    state.items = Array.isArray(items) ? items : [];
    state.page = 1;
    renderSectionPage(sectionName);
  }


  function extractDesignRecords(result) {
    const candidates = [
      result?.data?.records,
      result?.data?.designs,
      result?.data?.items,
      result?.data?.results,
      result?.data,
      result?.records,
      result?.designs,
      result?.items,
      result?.results,
      result?.result?.records,
      result?.result?.designs,
      result?.result?.items,
      result?.result?.results,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }

    return [];
  }

  function extractHomeFallbackDesigns(homeData) {
    if (!homeData || typeof homeData !== "object") return [];

    return dedupeDesigns([
      ...(Array.isArray(homeData.featuredPE) ? homeData.featuredPE : []),
      ...(Array.isArray(homeData.featuredSpiritWear) ? homeData.featuredSpiritWear : []),
      ...(Array.isArray(homeData.trending) ? homeData.trending : []),
    ]);
  }

  function normalizeSegment(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");
  }

  function isPESegment(design) {
    const value = normalizeSegment(design?.segment);
    return value === "pe uniforms" || value === "pe uniform";
  }

  function isSpiritSegment(design) {
    const value = normalizeSegment(design?.segment);
    return value === "spirit wear" || value === "spiritwear";
  }

  function dedupeDesigns(items) {
    const seen = new Set();

    return (Array.isArray(items) ? items : []).filter((item) => {
      const key =
        String(item?.inksoftDesignId || "") ||
        String(item?.slug || "") ||
        String(item?.id || "");

      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function getDesignTimestamp(design) {
    const candidates = [
      design?.publishedAt,
      design?.publishDate,
      design?.updatedAt,
      design?.lastSyncedAt,
      design?.createdAt,
    ];

    for (const value of candidates) {
      const timestamp = Date.parse(value || "");
      if (Number.isFinite(timestamp)) return timestamp;
    }

    return 0;
  }

  function sortDesignsNewestFirst(items) {
    return [...items].sort((a, b) => {
      const dateDifference = getDesignTimestamp(b) - getDesignTimestamp(a);
      if (dateDifference !== 0) return dateDifference;

      const aId = Number(a?.inksoftDesignId || a?.designId || 0);
      const bId = Number(b?.inksoftDesignId || b?.designId || 0);
      if (aId !== bId) return bId - aId;

      return String(a?.title || "").localeCompare(String(b?.title || ""));
    });
  }

  function updateFeaturedScrollerButtons() {
    const scroller = els.featuredScroller;
    if (!scroller) return;

    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const atStart = scroller.scrollLeft <= 2;
    const atEnd = scroller.scrollLeft >= maxScrollLeft - 2;

    if (els.featuredPrev) els.featuredPrev.disabled = atStart;
    if (els.featuredNext) els.featuredNext.disabled = atEnd || maxScrollLeft <= 2;
  }

  function renderFeaturedScroller(items) {
    if (!els.featuredScroller || !els.featuredSection) return;

    const featuredItems = (Array.isArray(items) ? items : [])
      .filter((item) => item?.featured === true)
      .slice(0, FEATURED_LIMIT);

    if (!featuredItems.length) {
      els.featuredSection.hidden = true;
      els.featuredScroller.innerHTML = "";
      return;
    }

    els.featuredSection.hidden = false;
    els.featuredScroller.innerHTML = featuredItems
      .map((item, index) => buildDesignCardSafe(item, "featured", 1, index + 1))
      .join("");

    requestAnimationFrame(updateFeaturedScrollerButtons);
  }

  function scrollFeatured(direction) {
    const scroller = els.featuredScroller;
    if (!scroller) return;

    const firstCard = scroller.querySelector(".di-design-card");
    const styles = window.getComputedStyle(scroller);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || "20") || 20;
    const cardWidth = firstCard?.getBoundingClientRect().width || scroller.clientWidth / 4;
    const visibleCards = Math.max(1, Math.round(scroller.clientWidth / (cardWidth + gap)));
    const step = (cardWidth + gap) * visibleCards;

    scroller.scrollBy({
      left: direction * step,
      behavior: "smooth",
    });
  }


  let DEEP_LINK_DESIGNS_CACHE = [];

  function normalizeDeepLinkDesignKey(value) {
    const raw = String(value == null ? "" : value).trim();
    if (!raw) return "";

    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch (error) {
      decoded = raw;
    }

    decoded = decoded.trim();

    const prefixed = decoded.match(/^DI[-_:]?(\d+)$/i);
    if (prefixed && prefixed[1]) return prefixed[1];

    const numeric = decoded.match(/(\d{6,})/);
    if (numeric && numeric[1]) return numeric[1];

    return decoded.replace(/^DI[-_:]?/i, "").trim();
  }

  function getDesignLookupValues(design) {
    if (!design || typeof design !== "object") return [];

    return Array.from(
      new Set(
        [
          design.slug,
          design.id,
          design.ID,
          design.designId,
          design.DesignId,
          design.inksoftDesignId,
          design.inksoft_design_id,
          design.artworkId,
          design.ArtworkId,
          design.title,
          design.name,
          extractDesignId(design),
        ]
          .map((item) => String(item == null ? "" : item).trim())
          .filter(Boolean)
      )
    );
  }

  function findDesignForDeepLink(value) {
    const raw = String(value == null ? "" : value).trim();
    const normalized = normalizeDeepLinkDesignKey(raw);
    if (!raw && !normalized) return null;

    return DEEP_LINK_DESIGNS_CACHE.find((design) => {
      const values = getDesignLookupValues(design);

      return values.some((candidate) => {
        return (
          candidate === raw ||
          candidate === normalized ||
          normalizeDeepLinkDesignKey(candidate) === normalized
        );
      });
    }) || null;
  }


  function normalizeFilterText(value) {
    return String(value == null ? "" : value)
      .toLowerCase()
      .replace(/&amp;/g, "&")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function firstDesignValue(design, keys) {
    for (const key of keys) {
      const value = design && design[key];
      if (Array.isArray(value) && value.length) return String(value[0] || "").trim();
      const text = String(value == null ? "" : value).trim();
      if (text) return text;
    }
    return "";
  }

  function arrayDesignValues(design, keys) {
    const output = [];
    const seen = new Set();

    keys.forEach((key) => {
      const value = design && design[key];
      const items = Array.isArray(value) ? value : String(value == null ? "" : value).split(/[;,|]/g);

      items.forEach((item) => {
        const text = String(item == null ? "" : item).trim();
        const normalized = normalizeFilterText(text);
        if (!text || seen.has(normalized)) return;
        seen.add(normalized);
        output.push(text);
      });
    });

    return output;
  }

  function getDesignSegment(design) {
    const direct = firstDesignValue(design, [
      "segment",
      "productGroup",
      "product_group",
      "sourceCategoryName",
      "sourceCategory",
      "categoryName",
      "category"
    ]);

    const text = normalizeFilterText(
      [
        direct,
        firstDesignValue(design, ["title", "name", "publicTitle", "artworkTitle"]),
        arrayDesignValues(design, ["tags", "keywords"]).join(" ")
      ].join(" ")
    );

    if (text.includes("spirit")) return "Spirit Wear";
    if (text.includes("athletic") || text.includes("athletics")) return "Athletics";
    if (text.includes("club") || text.includes("organization")) return "Clubs & Organizations";
    if (text.includes("grad") || text.includes("graduation")) return "Grad Apparel";
    if (text.includes("event")) return "Events";
    if (text.includes("pe uniform") || text.includes("phys ed") || text.includes("p e ")) return "PE Uniforms";

    return direct;
  }

  function getDesignCollection(design) {
    return firstDesignValue(design, [
      "collection",
      "collectionName",
      "theme",
      "sourceCategoryName",
      "categoryName"
    ]);
  }

  function getDesignMascot(design) {
    const direct = firstDesignValue(design, ["mascot", "schoolMascot"]);
    if (direct) return direct;

    const title = firstDesignValue(design, ["title", "name", "publicTitle", "artworkTitle"]);
    const commonMascots = [
      "Falcons", "Eagles", "Tigers", "Lions", "Panthers", "Bulldogs", "Cougars",
      "Wolves", "Bears", "Knights", "Warriors", "Sharks", "Spartans", "Jaguars",
      "Dragons", "Mustangs", "Patriots", "Rams", "Hawks", "Wildcats", "Trojans",
      "Roadrunners", "Cardinals", "Hornets", "Bobcats", "Dolphins", "Ravens"
    ];

    const titleText = normalizeFilterText(title);
    const found = commonMascots.find((mascot) => titleText.includes(normalizeFilterText(mascot)));
    return found || "";
  }

  function getDesignStyle(design) {
    return firstDesignValue(design, [
      "designStyle",
      "style",
      "theme",
      "designType",
      "collection"
    ]);
  }

  function getDesignActivity(design) {
    return firstDesignValue(design, [
      "activity",
      "sport",
      "sports",
      "season",
      "audience"
    ]);
  }

  function getDesignApparelTypes(design) {
    const values = arrayDesignValues(design, [
      "apparelTypes",
      "allowedApparelTypes",
      "allowed_apparel_set",
      "productTypes",
      "recommendedProducts"
    ]);

    if (values.length) return values;

    const text = normalizeFilterText([
      firstDesignValue(design, ["title", "name", "publicTitle", "artworkTitle"]),
      arrayDesignValues(design, ["tags", "keywords"]).join(" ")
    ].join(" "));

    const inferred = [];
    if (text.includes("hoodie") || text.includes("pullover")) inferred.push("Pullover Sweatshirt");
    if (text.includes("crewneck")) inferred.push("Crewneck Sweatshirt");
    if (text.includes("polo")) inferred.push("Polo");
    if (text.includes("pant") || text.includes("sweatpant")) inferred.push("Pants");
    if (text.includes("tote")) inferred.push("Tote Bag");
    if (text.includes("cinch")) inferred.push("Cinch Bag");
    if (!inferred.length) inferred.push("T-Shirt");

    return inferred;
  }

  function getDesignSearchText(design) {
    const values = [
      firstDesignValue(design, ["designId", "inksoftDesignId", "id", "slug"]),
      customizableReference(design),
      firstDesignValue(design, ["title", "name", "publicTitle", "artworkTitle"]),
      firstDesignValue(design, ["description", "subtitle", "notes"]),
      firstDesignValue(design, ["schoolName", "school", "organizationName", "organization"]),
      getDesignMascot(design),
      getDesignSegment(design),
      getDesignCollection(design),
      getDesignStyle(design),
      getDesignActivity(design),
      getDesignApparelTypes(design).join(" "),
      arrayDesignValues(design, ["tags", "keywords", "primaryColors", "colors"]).join(" ")
    ];

    return normalizeFilterText(values.join(" "));
  }

  function matchesFilterValue(actualValue, selectedValue) {
    const selected = normalizeFilterText(selectedValue);
    if (!selected) return true;

    if (Array.isArray(actualValue)) {
      return actualValue.some((item) => normalizeFilterText(item).includes(selected));
    }

    return normalizeFilterText(actualValue).includes(selected);
  }

  function designMatchesFilters(design) {
    const filters = designSearchState.filters;

    if (
      filters.search &&
      !getDesignSearchText(design).includes(normalizeFilterText(filters.search))
    ) return false;

    const selectedCategories = Array.isArray(filters.categories)
      ? filters.categories.filter(Boolean)
      : [];

    if (!selectedCategories.length) return true;

    const designCategories = getDesignSimpleCategories(design);

    return selectedCategories.some((category) =>
      designCategories.includes(String(category || "").toLowerCase())
    );
  }

  function uniqueFacetValues(designs, getter) {
    const map = new Map();

    designs.forEach((design) => {
      const raw = getter(design);
      const values = Array.isArray(raw) ? raw : [raw];

      values.forEach((item) => {
        const text = String(item == null ? "" : item).trim();
        const normalized = normalizeFilterText(text);
        if (!text || !normalized) return;
        map.set(normalized, text);
      });
    });

    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }

  function buildFilterOptions(values, currentValue, emptyLabel) {
    const current = normalizeFilterText(currentValue);
    return [`<option value="">${escapeHtml(emptyLabel)}</option>`]
      .concat(values.map((value) => {
        const selected = normalizeFilterText(value) === current ? " selected" : "";
        return `<option value="${escapeHtml(value)}"${selected}>${escapeHtml(value)}</option>`;
      }))
      .join("");
  }

  function ensureDesignSearchStyles() {
    if (document.getElementById("di-public-search-styles")) return;

    const style = document.createElement("style");
    style.id = "di-public-search-styles";
    style.textContent = `
      #DesignInspirationCenter .di-search-panel {
        margin: 18px 0 26px;
        padding: 18px;
        border: 1px solid #dbe5f1;
        border-radius: 18px;
        background: #ffffff;
        box-shadow: 0 12px 30px rgba(15, 23, 42, 0.04);
      }
      #DesignInspirationCenter .di-search-panel__row {
        display: grid;
        grid-template-columns: minmax(260px, 1fr) auto;
        gap: 14px;
        align-items: end;
      }
      #DesignInspirationCenter .di-search-field { display: grid; gap: 6px; }
      #DesignInspirationCenter .di-search-field label,
      #DesignInspirationCenter .di-search-filter-label {
        color: #64748b;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: .05em;
        text-transform: uppercase;
      }
      #DesignInspirationCenter .di-search-input {
        min-height: 46px;
        width: 100%;
        border: 1px solid #dbe5f1;
        border-radius: 10px;
        background: #ffffff;
        color: #111827;
        font: inherit;
        padding: 10px 12px;
      }
      #DesignInspirationCenter .di-search-input:focus {
        border-color: #173f7c;
        outline: 3px solid rgba(23, 63, 124, .14);
      }
      #DesignInspirationCenter .di-simple-filters {
        display: flex;
        flex-wrap: wrap;
        gap: 9px;
        margin-top: 14px;
      }
      #DesignInspirationCenter .di-simple-filter {
        position: relative;
        display: inline-flex;
        align-items: center;
      }
      #DesignInspirationCenter .di-simple-filter input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }
      #DesignInspirationCenter .di-simple-filter span {
        display: inline-flex;
        align-items: center;
        min-height: 40px;
        padding: 9px 13px;
        border: 1px solid #cbd5e1;
        border-radius: 999px;
        background: #ffffff;
        color: #334155;
        cursor: pointer;
        font-size: 13px;
        font-weight: 800;
        transition: .16s ease;
      }
      #DesignInspirationCenter .di-simple-filter input:checked + span {
        border-color: #173f7c;
        background: #173f7c;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(23, 63, 124, .18);
      }
      #DesignInspirationCenter .di-simple-filter input:focus-visible + span {
        outline: 3px solid rgba(37, 99, 235, .24);
        outline-offset: 2px;
      }
      #DesignInspirationCenter .di-search-actions { display: flex; gap: 10px; }
      #DesignInspirationCenter .di-search-button {
        min-height: 46px;
        border: 0;
        border-radius: 10px;
        background: #173f7c;
        color: #ffffff;
        cursor: pointer;
        font: inherit;
        font-weight: 800;
        padding: 10px 15px;
      }
      #DesignInspirationCenter .di-search-button--secondary { background: #334155; }
      #DesignInspirationCenter .di-search-button[hidden] { display: none !important; }
      #DesignInspirationCenter .di-search-count {
        margin-top: 12px;
        color: #64748b;
        font-size: 13px;
      }
      #DesignInspirationCenter .di-customizable-reference {
        margin-top: 7px;
        color: #9a4d00;
        font-size: 11px;
        font-weight: 900;
        letter-spacing: .04em;
        text-transform: uppercase;
      }
      #DesignInspirationCenter .di-empty-state--search {
        grid-column: 1 / -1;
        padding: 32px;
        border: 1px dashed #cbd5e1;
        border-radius: 16px;
        background: #f8fafc;
        color: #475569;
        text-align: center;
      }
      @media screen and (max-width: 760px) {
        #DesignInspirationCenter .di-search-panel__row { grid-template-columns: 1fr; }
        #DesignInspirationCenter .di-search-actions,
        #DesignInspirationCenter .di-search-button { width: 100%; }
      }
    `;
    document.head.appendChild(style);
  }

  function ensureDesignSearchPanel() {
    ensureDesignSearchStyles();

    let panel = document.getElementById("di-design-search-panel");
    if (panel) return panel;

    const allDesignsSection = document.getElementById("di-all-designs-section");
    if (!allDesignsSection) return null;

    const head = allDesignsSection.querySelector(".di-section__head");
    panel = document.createElement("div");
    panel.id = "di-design-search-panel";
    panel.className = "di-search-panel";
    panel.setAttribute("aria-label", "Search and filter design ideas");

    if (head && head.nextSibling) {
      allDesignsSection.insertBefore(panel, head.nextSibling);
    } else if (head) {
      allDesignsSection.appendChild(panel);
    } else {
      allDesignsSection.insertBefore(panel, allDesignsSection.firstChild);
    }

    return panel;
  }

  function renderDesignSearchPanel() {
    const panel = ensureDesignSearchPanel();
    if (!panel) return;

    const filters = designSearchState.filters;
    const categories = Array.isArray(filters.categories) ? filters.categories : [];
    const checked = (value) => categories.includes(value) ? " checked" : "";
    const hasActiveFilters = Boolean(String(filters.search || "").trim() || categories.length);

    panel.innerHTML = `
      <div class="di-search-panel__row">
        <div class="di-search-field">
          <label for="di-design-search-input">Search design ideas</label>
          <input
            id="di-design-search-input"
            class="di-search-input"
            type="search"
            value="${escapeHtml(filters.search)}"
            placeholder="Search school, mascot, organization, activity, title or Design ID..."
          >
        </div>

        <div class="di-search-actions">
          <button id="di-design-clear-button" class="di-search-button di-search-button--secondary" type="button"${hasActiveFilters ? "" : " hidden"}>Clear Filters</button>
        </div>
      </div>

      <div class="di-search-filter-label">Filter by type</div>
      <div class="di-simple-filters" role="group" aria-label="Design type filters">
        <label class="di-simple-filter">
          <input type="checkbox" value="customizable" data-di-simple-filter${checked("customizable")}>
          <span>Customizable Designs</span>
        </label>
        <label class="di-simple-filter">
          <input type="checkbox" value="school" data-di-simple-filter${checked("school")}>
          <span>School</span>
        </label>
        <label class="di-simple-filter">
          <input type="checkbox" value="business" data-di-simple-filter${checked("business")}>
          <span>Business / Company</span>
        </label>
        <label class="di-simple-filter">
          <input type="checkbox" value="organization" data-di-simple-filter${checked("organization")}>
          <span>Organization / Teams / Groups</span>
        </label>
      </div>

      <div id="di-design-search-count" class="di-search-count" aria-live="polite"></div>
    `;

    bindDesignSearchPanelEvents();
    updateDesignSearchCount();
  }

  function readDesignSearchPanelValues() {
    /*
      v121:
      Preserve the user's exact search input while typing.

      Do not trim here. The search matcher already normalizes/trim values through
      normalizeFilterText(). Trimming the live field caused a trailing space to
      disappear whenever the debounced search panel re-rendered, turning entries
      such as "PE Uniform" into "PEUniform".
    */
    designSearchState.filters.search = String(
      document.getElementById("di-design-search-input")?.value || ""
    );

    designSearchState.filters.categories = Array.from(
      document.querySelectorAll("[data-di-simple-filter]:checked")
    ).map((input) => String(input.value || "").trim()).filter(Boolean);
  }

  function updateDesignSearchCount() {
    const count = document.getElementById("di-design-search-count");
    if (!count) return;

    const total = designSearchState.allDesigns.length;
    const filtered = designSearchState.filteredDesigns.length;

    count.textContent = filtered === total
      ? `${total} design ideas available.`
      : `${filtered} of ${total} design ideas match your search.`;
  }

  function renderFilteredDesigns(scrollIntoView = false) {
    const filtered = designSearchState.allDesigns.filter(designMatchesFilters);
    designSearchState.filteredDesigns = filtered;

    if (!filtered.length) {
      const grid = sectionState.allDesigns.grid();
      if (grid) {
        grid.innerHTML = `
          <div class="di-empty-state di-empty-state--search">
            No design ideas match your current search. Try clearing filters or using a broader mascot, school, category, or style keyword.
          </div>
        `;
      }

      sectionState.allDesigns.items = [];
      sectionState.allDesigns.page = 1;
      renderSectionPager("allDesigns");
      updateDesignSearchCount();
      return;
    }

    setSectionItems("allDesigns", filtered);
    updateDesignSearchCount();

    if (scrollIntoView && els.allDesigns) {
      const section = els.allDesigns.closest(".di-section");
      if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function applyDesignSearchFilters(scrollIntoView = false) {
    readDesignSearchPanelValues();
    renderFilteredDesigns(scrollIntoView);
  }

  function clearDesignSearchFilters() {
    designSearchState.filters = {
      search: "",
      categories: []
    };

    renderDesignSearchPanel();
    renderFilteredDesigns(true);
  }

  /*
    v122 SEO/search deep link support:
    Allows crawlable landing pages to send visitors into a pre-filtered
    Design Inspiration Center using:
      ?search=PE%20Uniform
      ?search=Spirit%20Wear

    The query parameter is used only as an initial UI state. The canonical
    Design Inspiration page remains the main page, avoiding creation of
    indexable filter-page combinations.
  */
  function seedDesignSearchFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const search = String(params.get("search") || "");

      if (search) {
        designSearchState.filters.search = search;
        designSearchState.filters.categories = [];
      }
    } catch (error) {
      console.warn("[Design Inspiration] Unable to read search deep link.", error);
    }
  }

  function bindDesignSearchPanelEvents() {
    const searchInput = document.getElementById("di-design-search-input");
    const clearButton = document.getElementById("di-design-clear-button");
    const checkboxes = Array.from(document.querySelectorAll("[data-di-simple-filter]"));

    if (searchInput) {
      let timer = null;
      searchInput.addEventListener("input", () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          applyDesignSearchFilters(false);
          renderDesignSearchPanel();
          const restoredInput = document.getElementById("di-design-search-input");
          if (restoredInput) {
            restoredInput.focus();
            restoredInput.setSelectionRange(restoredInput.value.length, restoredInput.value.length);
          }
        }, 180);
      });

      searchInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          applyDesignSearchFilters(true);
          renderDesignSearchPanel();
        }
      });
    }

    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        applyDesignSearchFilters(false);
        renderDesignSearchPanel();
      });
    });

    if (clearButton) clearButton.addEventListener("click", clearDesignSearchFilters);
  }

  function setDesignCategoryFilter(categoryTitle) {
    const normalized = normalizeFilterText(categoryTitle);
    if (!normalized) return;

    let category = "";
    if (normalized.includes("school")) category = "school";
    else if (normalized.includes("business") || normalized.includes("company") || normalized.includes("corporate")) category = "business";
    else if (normalized.includes("organization") || normalized.includes("team") || normalized.includes("group") || normalized.includes("club")) category = "organization";

    designSearchState.filters.search = category ? "" : String(categoryTitle || "").trim();
    designSearchState.filters.categories = category ? [category] : [];

    renderDesignSearchPanel();
    renderFilteredDesigns(true);
  }


  function slugifyDesignValue(value) {
    return String(value == null ? "" : value)
      .trim()
      .toLowerCase()
      .replace(/&amp;/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function normalizePublicDesignRecordForFrontend(record) {
    if (!record || typeof record !== "object") return null;

    const designId = String(
      record.inksoftDesignId ||
        record.inksoft_design_id ||
        record.designId ||
        record.id ||
        ""
    ).trim();

    if (!designId) return null;

    const rawTitle = String(
      record.title ||
        record.name ||
        record.publicTitle ||
        record.artworkTitle ||
        ("Design " + designId)
    ).trim();

    const title = titleWithCustomizableReference(rawTitle, {
      ...record,
      designId,
      inksoftDesignId: designId
    });

    const slug =
      String(record.slug || "").trim() ||
      slugifyDesignValue([title, designId].filter(Boolean).join("-")) ||
      designId;

    const thumbnailUrl =
      record.thumbnailUrl ||
      record.previewUrl ||
      record.sourceArtworkUrl ||
      record.imageUrl ||
      record.previewImageUrl ||
      getSidePublisherArtwork(record, "Front");

    const tags = Array.isArray(record.tags)
      ? record.tags
      : String(record.tags || "").split(/[;,|]/g).map((item) => item.trim()).filter(Boolean);

    const apparelTypes = Array.isArray(record.apparelTypes)
      ? record.apparelTypes
      : String(record.apparelTypes || "").split(/[;,|]/g).map((item) => item.trim()).filter(Boolean);

    return {
      ...record,
      id: record.id || designId,
      designId,
      inksoftDesignId: designId,
      slug,
      title,
      name: record.name || title,
      description: record.description || "",
      segment: record.segment || "",
      collection: record.collection || "",
      designStyle: record.designStyle || record.style || "",
      style: record.style || record.designStyle || "",
      activity: record.activity || "",
      audience: record.audience || "",
      season: record.season || "",
      mascot: record.mascot || "",
      schoolName: record.schoolName || record.organizationName || "",
      organizationName: record.organizationName || record.schoolName || "",
      tags,
      apparelTypes,
      thumbnailUrl,
      previewUrl: record.previewUrl || thumbnailUrl,
      imageUrl: record.imageUrl || thumbnailUrl,
      previewImageUrl: record.previewImageUrl || thumbnailUrl,
      artworkViews: Array.isArray(record.artworkViews) && record.artworkViews.length
        ? record.artworkViews
        : [
            {
              side: "Front",
              label: "Front",
              artworkUrl:
                record.sourceArtworkUrl ||
                record.thumbnailUrl ||
                record.previewUrl ||
                getSidePublisherArtwork({ inksoftDesignId: designId }, "Front"),
              imageUrl:
                record.sourceArtworkUrl ||
                record.thumbnailUrl ||
                record.previewUrl ||
                getSidePublisherArtwork({ inksoftDesignId: designId }, "Front")
            }
          ],
      updatedAt: record.updatedAt || record.publishedAt || new Date().toISOString(),
      publishedAt: record.publishedAt || record.updatedAt || new Date().toISOString(),
      publicSafeSource: true
    };
  }

  function normalizePublicDesignRecordsForFrontend(records) {
    return (Array.isArray(records) ? records : [])
      .map(normalizePublicDesignRecordForFrontend)
      .filter(Boolean);
  }


  async function loadHome() {
    const homeResult = await fetchDesignApi("home");
    const data = homeResult?.data || homeResult || {};

    let designsResult = null;
    let rawDesigns = [];

    try {
      /*
       * v75 public-safe source:
       * The customer-facing Design Inspiration page must only show designs
       * approved from the Admin Design Inspiration Library.
       *
       * Public visibility rule is enforced by:
       *   /api/poc/design-inspiration/search
       *
       * Required record state:
       *   visibility = public
       *   published = true
       *   archived = false
       *
       * Do not fall back to /designs or home payload designs here, because those
       * older feeds include internal / needs-setup portfolio artwork.
       */
      designsResult = await fetchDesignApi("search?take=250");
      rawDesigns = normalizePublicDesignRecordsForFrontend(
        extractDesignRecords(designsResult)
      );
    } catch (error) {
      console.warn(
        "[Design Inspiration Center] Public-safe search endpoint failed. Public grid will remain empty to avoid exposing internal artwork.",
        error
      );
      rawDesigns = [];
    }

    if (!rawDesigns.length) {
      console.warn(
        "[Design Inspiration Center] Public-safe endpoint returned 0 published records. Publish designs from the Admin Library to display them publicly."
      );
    }

    console.log("[Design Inspiration Center] Home data:", data);
    console.log("[Design Inspiration Center] Raw design count:", rawDesigns.length);
    console.log("[Design Inspiration Center] First raw design:", rawDesigns[0]);

    const allDesigns = sortDesignsNewestFirst(
      dedupeDesigns(rawDesigns)
        .map(applyMatrixToDesign)
        .filter((design) => {
          const matrixArtwork = getMatrixArtworkForDesign(design);
          if (!matrixArtwork) return true;
          return matrixArtwork.active !== false && matrixArtwork.showInDesignGrid !== false;
        })
    );

    DEEP_LINK_DESIGNS_CACHE = allDesigns;
    window.UNISOURCE_DI_RENDERABLE_DESIGNS = allDesigns;

    console.log("[Design Inspiration Center] Renderable design count:", allDesigns.length);
    console.log("[Design Inspiration Center] First renderable design:", allDesigns[0]);

    if (data.hero) {
      if (els.heroHeadline) {
        els.heroHeadline.textContent =
          data.hero.headline || "Design Inspiration Center";
      }

      if (els.heroSubheadline) {
        els.heroSubheadline.textContent = data.hero.subheadline || "";
      }
    }

    repositionHomeSections();

    if (els.primaryCategories) {
      const allCategories = buildAllCategoryCards(
        allDesigns,
        data.primaryCategories
      );

      ensureCategoryRowStyles();

      els.primaryCategories.innerHTML = allCategories
        .map(buildCategoryCard)
        .join("");

      requestAnimationFrame(updateCategoryScrollButtons);
    }

    renderFeaturedScroller(allDesigns);

    designSearchState.allDesigns = allDesigns;
    designSearchState.filteredDesigns = allDesigns;
    seedDesignSearchFromUrl();
    renderDesignSearchPanel();
    renderFilteredDesigns(false);

    if (Array.isArray(data.howItWorks)) {
      renderHowItWorks(data.howItWorks);
    }

    return {
      total: allDesigns.length,
      featured: allDesigns.filter((design) => design?.featured === true).slice(0, FEATURED_LIMIT).length,
    };
  }

  async function loadDesign(slug) {
    const rawSlug = String(slug == null ? "" : slug).trim();
    const normalizedSlug = normalizeDeepLinkDesignKey(rawSlug);
    const matchedDesign = findDesignForDeepLink(rawSlug);

    /*
      v77:
      The public grid is already loaded from the public-safe search endpoint.
      That search record now includes the Admin-saved productReference,
      defaultMockup, primaryMockup, mockups, productId, styleId, colorName,
      and garment image fields.

      Important:
      Do NOT fetch /design/{slug} first for normal grid/modal clicks.
      Older single-design payloads can still contain legacy matrix/default data
      and can overwrite the Admin-saved product/style reference. This was why
      the modal still showed Gildan 2000 White even though the search endpoint
      returned the Admin values.

      For normal clicks, use the matched public-safe search record immediately.
      Only use /design/{slug} as a fallback for direct/deep links where the
      record is not already present in the renderable cache.
    */
    if (matchedDesign) {
      console.info("[Design Inspiration] Opening modal from public-safe search cache", {
        requested: rawSlug,
        normalized: normalizedSlug,
        slug: matchedDesign.slug,
        productId: matchedDesign.productId || matchedDesign.productReference?.productId || matchedDesign.defaultMockup?.productId || "",
        styleId: matchedDesign.styleId || matchedDesign.productReference?.styleId || matchedDesign.defaultMockup?.styleId || "",
        colorName: matchedDesign.colorName || matchedDesign.productReference?.colorName || matchedDesign.defaultMockup?.colorName || ""
      });

      return applyMatrixToDesign(matchedDesign);
    }

    const candidates = Array.from(
      new Set(
        [rawSlug, normalizedSlug]
          .map((item) => String(item == null ? "" : item).trim())
          .filter(Boolean)
      )
    );

    let lastError = null;

    for (const candidate of candidates) {
      try {
        const result = await fetchDesignApi(
          `design/${encodeURIComponent(candidate)}`
        );

        if (result && result.data) {
          return applyMatrixToDesign(result.data);
        }
      } catch (error) {
        lastError = error;
        console.warn("[Design Inspiration] Design lookup failed:", {
          requested: rawSlug,
          candidate,
          error,
        });
      }
    }

    if (lastError) {
      console.warn("[Design Inspiration] Unable to load deep-linked design", {
        requested: rawSlug,
        normalized: normalizedSlug,
        error: lastError,
      });
    }

    return null;
  }

  function openModal() {
    if (!els.modal) return;
    els.modal.style.display = "block";
    els.modal.setAttribute("aria-hidden", "false");
  }

  function closeModal() {
    if (!els.modal) return;
    els.modal.style.display = "none";
    els.modal.setAttribute("aria-hidden", "true");
  }



  function asCleanString(value) {
    return value == null ? "" : String(value).trim();
  }

  function normalizeInksoftLaunchUrl(url) {
    let value = asCleanString(url);
    if (!value) return "";

    value = value.replace(
      /\/Unisource_Apparel\/Unisource_Apparel\//g,
      "/Unisource_Apparel/"
    );

    if (/^https?:\/\//i.test(value)) return value;
    if (value.indexOf("/Unisource_Apparel/") === 0) {
      return "https://shop.unisourceapparel.com" + value;
    }

    return value;
  }

  function normalizeInksoftDesignId(value) {
    const raw = asCleanString(value);
    if (!raw) return "";

    // Design Inspiration card IDs may use DI-1748938, while InkSoft
    // design-map requires the numeric InkSoft design ID: 1748938.
    const prefixedMatch = raw.match(/^DI[-_:]?(\d+)$/i);
    if (prefixedMatch && prefixedMatch[1]) return prefixedMatch[1];

    const numericMatch = raw.match(/(\d{6,})/);
    if (numericMatch && numericMatch[1]) return numericMatch[1];

    return raw.replace(/^DI[-_:]?/i, "");
  }

  function getVisualizerDesignId(design) {
    const candidates = [
      design && design.designId,
      design && design.DesignId,
      design && design.inksoftDesignId,
      design && design.artworkId,
      design && design.ArtworkId,
      design && design.matrixArtwork && design.matrixArtwork.designId,
      design && design.id,
      design && design.ID,
    ];

    for (const candidate of candidates) {
      const value = normalizeInksoftDesignId(candidate);
      if (value) return value;
    }

    return "";
  }

  function getDesignArtworkId(design) {
    return String(
      (design && design.artworkId) ||
      (design && design.inksoftArtworkId) ||
      (design && design.designId) ||
      (design && design.inksoftDesignId) ||
      (design && design.id) ||
      ""
    ).trim();
  }

  function getDesignArtworkImageCandidates(design) {
    if (!design) return [];

    const views = getDesignArtworkViews(design);
    const frontView =
      views.find((item) => normalizeSideName(item && item.side) === "Front") ||
      views[0] ||
      null;

    /*
      v102:
      The Artwork Reference thumbnail must prefer InkSoft's transparent
      publisher PNG. A flattened thumbnail can contain a baked-in blue/white
      background that completely covers the Admin-defined preview color.
    */
    const publisherCandidates = getSidePublisherArtworkCandidates(design, "Front");
    const explicitTransparentCandidates = [
      design.transparentArtworkUrl,
      design.publisherArtworkUrl,
      design.frontPublisherArtworkUrl,
      design.sourceTransparentArtworkUrl,
      frontView && frontView.transparentArtworkUrl,
      frontView && frontView.publisherArtworkUrl,
      frontView && frontView.artworkUrl,
      getPublisherArtwork(design),
    ].filter((url) => isInksoftTransparentArtworkUrl(url));

    return uniqueImageList([
      ...explicitTransparentCandidates,
      ...publisherCandidates,

      /*
        Non-transparent and legacy images are fallback-only.
      */
      frontView && frontView.artworkUrl,
      frontView && frontView.fallbackUrl,
      ...(frontView && Array.isArray(frontView.fallbackUrls)
        ? frontView.fallbackUrls
        : []),
      design.artworkUrl,
      design.artworkImageUrl,
      design.sourceArtworkUrl,
      design.imageUrl,
      design.thumbnailUrl,
      design.thumbUrl,
      design.previewUrl,
      design.previewImageUrl,
      design.designImageUrl,
      getThumbnailArtwork(design),
    ]);
  }

  function getDesignArtworkImage(design) {
    return getDesignArtworkImageCandidates(design)[0] || "";
  }

  function normalizeArtworkBackgroundColor(value) {
    const color = String(value || "").trim();

    if (!color) return "";

    if (/^#[0-9a-f]{6}$/i.test(color)) {
      return color.toUpperCase();
    }

    if (/^[0-9a-f]{6}$/i.test(color)) {
      return `#${color.toUpperCase()}`;
    }

    return "";
  }

  function getDesignArtworkBackgroundColor(design) {
    if (!design || typeof design !== "object") return "";

    const candidates = [
      design.artworkBackgroundColor,
      design.previewBackgroundColor,
      design.artworkPreviewBackground,

      design.displayOverride && design.displayOverride.artworkBackgroundColor,
      design.displayOverride && design.displayOverride.backgroundColor,
      design.displayOverride && design.displayOverride.background,

      design.config && design.config.artworkBackgroundColor,
      design.designConfig && design.designConfig.artworkBackgroundColor,
      design.adminConfig && design.adminConfig.artworkBackgroundColor,

      design.rawDesign && design.rawDesign.artworkBackgroundColor,

      design.metadata && design.metadata.artworkBackgroundColor,
      design.settings && design.settings.artworkBackgroundColor
    ];

    for (const candidate of candidates) {
      const normalized = normalizeArtworkBackgroundColor(candidate);
      if (normalized) return normalized;
    }

    return "";
  }

  function buildArtworkReferenceHtml(design, artworkViews) {
    const artworkId = getDesignArtworkId(design);
    const artworkImageCandidates = getDesignArtworkImageCandidates(design);
    const artworkImageUrl = artworkImageCandidates[0] || "";
    const artworkFallbackUrl = artworkImageCandidates[1] || "";
    const artworkBackgroundColor =
      getDesignArtworkBackgroundColor(design) || "#FFFFFF";

    const viewLabel = (artworkViews || [])
      .map((item) => item && item.side)
      .filter(Boolean)
      .join(", ") || "Front";

    /*
      Inline !important is intentional here because older theme CSS may set
      the preview tile to a fixed pale-blue or white background.
    */
    const previewStyle = [
      `background:${artworkBackgroundColor} !important`,
      `background-color:${artworkBackgroundColor} !important`,
      "display:flex",
      "align-items:center",
      "justify-content:center"
    ].join(";");

    return `
      <div class="di-visualizer__section di-visualizer__artwork-reference">
        ${customizationBadgeLabel(design) ? `<div><span class="di-customizable-badge di-visualizer__customizable-badge">${escapeHtml(customizationBadgeLabel(design))}</span></div>` : ""}
        <div class="di-visualizer__label">Artwork Reference</div>
        <div class="di-visualizer__meta di-visualizer__reference">
          ${
            artworkId
              ? `<div><strong>Artwork ID:</strong> ${escapeHtml(artworkId)}</div>`
              : `<div class="di-muted">Artwork ID not available.</div>`
          }
          <div><strong>Artwork Views:</strong> ${escapeHtml(viewLabel)}</div>
          ${
            artworkImageUrl
              ? `
                <div
                  class="di-artwork-preview-wrap"
                  style="${escapeHtml(previewStyle)}"
                  data-artwork-background="${escapeHtml(artworkBackgroundColor)}"
                >
                  <img
                    class="di-artwork-preview"
                    src="${escapeHtml(artworkImageUrl)}"
                    data-fallback-src="${escapeHtml(artworkFallbackUrl)}"
                    alt="${escapeHtml((design && (design.title || design.name)) || "Artwork preview")} artwork"
                    loading="lazy"
                    onerror="
                      if (this.dataset.fallbackSrc && this.src !== this.dataset.fallbackSrc) {
                        var nextSrc = this.dataset.fallbackSrc;
                        this.dataset.fallbackSrc = '';
                        this.src = nextSrc;
                      } else {
                        this.style.display = 'none';
                      }
                    "
                  >
                </div>
              `
              : `<div class="di-muted">No artwork preview available.</div>`
          }
        </div>
      </div>
    `;
  }

  function buildInksoftShareStudioUrl(productId, styleId, designId) {
    const safeProductId = asCleanString(productId);
    const safeStyleId = asCleanString(styleId);
    const safeDesignId = normalizeInksoftDesignId(designId);

    if (!safeProductId || !safeStyleId) return "";

    let url =
      "https://shop.unisourceapparel.com/Unisource_Apparel/shop/design-studio/share/" +
      encodeURIComponent(safeProductId) +
      "/" +
      encodeURIComponent(safeStyleId);

    if (safeDesignId) url += "/" + encodeURIComponent(safeDesignId);
    return url;
  }

  function buildDesignerWrapperUrl(productId, styleId, designId) {
    const params = new URLSearchParams();
    params.set("productId", asCleanString(productId));
    params.set("styleId", asCleanString(styleId));
    if (asCleanString(designId)) params.set("designId", asCleanString(designId));
    return "/pages/design-studio?" + params.toString();
  }

  async function resolveVisualizerMappedDesign(productId, styleId, templateDesignId) {
    const safeProductId = asCleanString(productId);
    const safeStyleId = asCleanString(styleId);
    const safeTemplateDesignId = normalizeInksoftDesignId(templateDesignId);

    if (!safeProductId || !safeStyleId || !safeTemplateDesignId) {
      return {
        success: false,
        reason: "Missing Product ID, Style ID, or template Design ID.",
        shareUrl: "",
        mappedDesignId: "",
        raw: null,
      };
    }

    if (
      !window.InksoftPOC ||
      !window.InksoftPOC.api ||
      typeof window.InksoftPOC.api.getDesignMap !== "function"
    ) {
      return {
        success: false,
        reason: "InksoftPOC.api.getDesignMap is unavailable.",
        shareUrl: "",
        mappedDesignId: "",
        raw: null,
      };
    }

    try {
      const payload = await window.InksoftPOC.api.getDesignMap({
        productId: safeProductId,
        styleId: safeStyleId,
        designId: safeTemplateDesignId,
      });

      const data = payload && payload.data ? payload.data : payload;
      const shareUrl = normalizeInksoftLaunchUrl(
        data && (
          data.shareUrl ||
          data.ShareUrl ||
          data.launchUrl ||
          data.LaunchUrl ||
          data.href ||
          data.Href ||
          ""
        )
      );
      const mappedDesignId = asCleanString(
        data && (
          data.mappedDesignId ||
          data.MappedDesignId ||
          data.designId ||
          data.DesignId ||
          data.id ||
          data.ID ||
          ""
        )
      );

      return {
        // Some backend versions return a valid shareUrl without success:true.
        success: Boolean(shareUrl),
        reason: asCleanString(data && (data.note || data.message || data.error)),
        shareUrl,
        mappedDesignId,
        raw: data || null,
      };
    } catch (error) {
      return {
        success: false,
        reason: error && error.message ? error.message : "Design mapping failed.",
        shareUrl: "",
        mappedDesignId: "",
        raw: null,
      };
    }
  }

  async function launchVisualizerDesignStudio(design, selectedRecord, trigger) {
    /*
      Exact-style launch fix:
      Keep the Visualizer's selected Product ID + Style ID as the source of truth.
      The design-map response may return a useful mapped design ID, but its URL
      must not be allowed to replace the selected apparel style/color route.

      This change is intentionally isolated to the Design Inspiration launch.
      It does not alter modal rendering, apparel selection, placement, paging,
      search/filtering, product-page behavior, or the shared Designer embed.
    */
    const productId = asCleanString(selectedRecord && selectedRecord.productId);
    const styleId = asCleanString(selectedRecord && selectedRecord.styleId);
    const colorName = asCleanString(
      selectedRecord && (
        selectedRecord.colorName ||
        selectedRecord.selectedColor ||
        selectedRecord.colorFamily ||
        selectedRecord.color
      )
    );
    const apparelKey = asCleanString(
      selectedRecord && selectedRecord.key
    ) || [productId, styleId, colorName.toLowerCase()]
      .filter(Boolean)
      .join("|");
    const templateDesignId = getVisualizerDesignId(design);

    if (!productId || !styleId || !templateDesignId) {
      setStatus(
        "Unable to open Design Online because the selected Product ID, Style ID, or Design ID is missing.",
        "error"
      );
      console.error("[Design Inspiration] Missing launch values", {
        productId,
        styleId,
        colorName,
        apparelKey,
        templateDesignId,
        selectedRecord,
        design,
      });
      return;
    }

    if (trigger) {
      trigger.setAttribute("aria-busy", "true");
      trigger.classList.add("is-loading");
      trigger.textContent = "Preparing Design Studio…";
    }

    try {
      const mapping = await resolveVisualizerMappedDesign(
        productId,
        styleId,
        templateDesignId
      );

      /*
        Use the mapped design ID when available, but always rebuild the final
        share URL with the CURRENT Visualizer product/style. This prevents a
        mapped response or stale InkSoft session URL from opening another color.
      */
      const finalDesignId = normalizeInksoftDesignId(
        mapping.mappedDesignId || templateDesignId
      );

      const finalLaunchUrl = buildInksoftShareStudioUrl(
        productId,
        styleId,
        finalDesignId
      );

      if (!finalLaunchUrl) {
        throw new Error(
          mapping.reason ||
          "InkSoft could not prepare this artwork for the selected apparel and color."
        );
      }

      console.log("[Design Inspiration] Exact apparel Designer launch", {
        requestedProductId: productId,
        requestedStyleId: styleId,
        requestedColor: colorName,
        apparelKey,
        templateDesignId,
        mappedDesignId: mapping.mappedDesignId || "",
        mappedShareUrl: mapping.shareUrl || "",
        finalDesignId,
        finalLaunchUrl,
      });

      if (mapping.shareUrl && mapping.shareUrl !== finalLaunchUrl) {
        console.warn(
          "[Design Inspiration] Ignoring mapped share URL so the selected Visualizer apparel style remains exact.",
          {
            mappedShareUrl: mapping.shareUrl,
            exactStyleLaunchUrl: finalLaunchUrl,
            productId,
            styleId,
            colorName,
          }
        );
      }

      closeModal();

      /*
        v113:
        Launch InkSoft as a standalone top-level page instead of embedding it
        inside the Shopify iframe.

        The iframe caused persistent ghost/loading modal artifacts during the
        quantity and cart stages. InkSoft works normally as a standalone page.
      */
      const designStudioWindow = window.open(finalLaunchUrl, "_blank");

      /*
        Some browsers may block the new tab. When that happens, continue in the
        current tab so the customer can still access Design Studio.
      */
      if (!designStudioWindow) {
        window.location.assign(finalLaunchUrl);
        return;
      }

      /*
        Disconnect the new page from the Shopify opener where supported.
        This avoids changing any of the existing preparation or mapping logic.
      */
      try {
        designStudioWindow.opener = null;
      } catch (error) {
        console.warn(
          "[Design Inspiration] Unable to clear Design Studio opener.",
          error
        );
      }

      return;
    } catch (error) {
      console.error("[Design Inspiration] Unable to prepare Design Studio.", error);
      setStatus(
        error && error.message
          ? error.message
          : "Unable to prepare Design Studio. Please try again.",
        "error"
      );
      alert(
        error && error.message
          ? error.message
          : "Unable to prepare Design Studio. Please try again."
      );
    } finally {
      if (trigger && document.body.contains(trigger)) {
        trigger.removeAttribute("aria-busy");
        trigger.classList.remove("is-loading");
        trigger.textContent = "Design Online & Order";
      }
    }
  }

  function getProductPageTransparentArtworkUrl(design) {
    if (!design) return "";

    const views = getDesignArtworkViews(design);
    const frontView =
      views.find((item) => normalizeSideName(item && item.side) === "Front") ||
      views[0] ||
      null;

    const candidates = uniqueImageList([
      ...getSidePublisherArtworkCandidates(design, "Front"),
      frontView && frontView.artworkUrl,
      frontView && frontView.fallbackUrl,
      ...(frontView && Array.isArray(frontView.fallbackUrls) ? frontView.fallbackUrls : []),
      design.exactTransparentArtworkUrl,
      design.transparentArtworkUrl,
      design.designArtworkUrl,
      design.cdnArtworkUrl,
      design.frontArtworkUrl,
      design.front_artwork_url,
      design.artworkUrl,
      design.thumbnailUrl,
      design.previewImageUrl,
      design.imageUrl,
    ]);

    return (
      candidates.find((url) => isInksoftTransparentArtworkUrl(url)) ||
      candidates[0] ||
      ""
    );
  }

  function bindVisualizerControls(design) {
    const root = document.querySelector("[data-visualizer-root]");
    const preview = document.querySelector("[data-visualizer-preview]");
    const categoryLabel = document.querySelector("[data-visualizer-category-label]");
    const colorOptions = document.querySelector("[data-visualizer-color-options]");
    const styleInfo = document.querySelector("[data-visualizer-style-info]");
    const designOnlineLink = document.querySelector("[data-design-online-link]");

    if (!root || !preview) return;

    const availableRecords = getAvailableApparelRecords(design);
    const groups = groupApparelByCategory(availableRecords);

    let selectedRecord = getDefaultApparel(design);

    function refreshColorOptions() {
      if (!colorOptions || !selectedRecord) return;

      const selectedGroupKey = apparelProductGroupKey(selectedRecord);
      const records = uniqueProductColorRecords(availableRecords).filter(
        (item) => apparelProductGroupKey(item) === selectedGroupKey
      );

      colorOptions.innerHTML = records
        .map((item) => {
          return `
            <button
              type="button"
              class="di-visualizer__option${item.key === selectedRecord.key ? " is-active" : ""}"
              data-visualizer-style="${escapeHtml(item.key)}"
              title="${escapeHtml(item.colorName)}"
            >
              ${escapeHtml(exactApprovedColorLabel(item))}
            </button>
          `;
        })
        .join("");
    }

    function refresh() {
      if (!selectedRecord) return;

      preview.innerHTML = buildMockup(design, selectedRecord);

      root.querySelectorAll("[data-visualizer-category]").forEach((button) => {
        button.classList.toggle(
          "is-active",
          button.dataset.visualizerCategory === apparelProductGroupKey(selectedRecord)
        );
      });

      refreshColorOptions();

      if (categoryLabel) {
        categoryLabel.textContent = apparelProductGroupLabel(selectedRecord);
      }

      if (styleInfo) {
        const productDetailParams = new URLSearchParams();
        productDetailParams.set("id", asCleanString(selectedRecord.productId));

        if (asCleanString(selectedRecord.styleId)) {
          productDetailParams.set("styleId", asCleanString(selectedRecord.styleId));
        }

        if (asCleanString(selectedRecord.colorName)) {
          productDetailParams.set("color", asCleanString(selectedRecord.colorName));
        }

        const productPageDesignId = getVisualizerDesignId(design);
        const productPageArtworkUrl = getProductPageTransparentArtworkUrl(design);

        if (productPageDesignId) {
          productDetailParams.set("designId", productPageDesignId);
          productDetailParams.set("inksoftDesignId", productPageDesignId);
        }

        if (productPageArtworkUrl) {
          productDetailParams.set("transparentArtworkUrl", productPageArtworkUrl);
          productDetailParams.set("designArtworkUrl", productPageArtworkUrl);
        }

        if (asCleanString(design && design.title)) {
          productDetailParams.set("designTitle", asCleanString(design.title));
        }

        const productDetailUrl = `/pages/inksoft-product?${productDetailParams.toString()}`;

        styleInfo.innerHTML = `
          <a
            class="di-visualizer__product-link"
            href="${escapeHtml(productDetailUrl)}"
            aria-label="View ${escapeHtml(selectedRecord.productName || "selected product")}"
          >
            <span class="di-visualizer__product-link-name">
              ${escapeHtml(selectedRecord.brand)} ${escapeHtml(selectedRecord.mfgSku)}
            </span>
            <span class="di-visualizer__product-link-meta">
              ${escapeHtml(selectedRecord.colorName)} · Product ID ${escapeHtml(selectedRecord.productId)} · Style ID ${escapeHtml(selectedRecord.styleId)}
            </span>
            <span class="di-visualizer__product-link-description">
              ${escapeHtml(selectedRecord.productName)}
            </span>
            <span class="di-visualizer__product-link-action">View product details →</span>
          </a>
        `;
      }

      if (designOnlineLink) {
        designOnlineLink.href = "#";
        designOnlineLink.dataset.productId = selectedRecord.productId || "";
        designOnlineLink.dataset.styleId = selectedRecord.styleId || "";
        designOnlineLink.dataset.designId = getVisualizerDesignId(design);
        designOnlineLink.removeAttribute("target");
        designOnlineLink.removeAttribute("rel");
      }
    }

    root.addEventListener("click", async (event) => {
      const designOnlineButton = event.target.closest("[data-design-online-link]");
      const categoryButton = event.target.closest("[data-visualizer-category]");
      const styleButton = event.target.closest("[data-visualizer-style]");

      if (designOnlineButton) {
        event.preventDefault();
        event.stopPropagation();
        await launchVisualizerDesignStudio(design, selectedRecord, designOnlineButton);
        return;
      }

      if (categoryButton) {
        const category = categoryButton.dataset.visualizerCategory;
        selectedRecord = getFirstRecordForCategory(
          availableRecords,
          category,
          selectedRecord
        );
        refresh();
        return;
      }

      if (styleButton) {
        const key = styleButton.dataset.visualizerStyle;
        const record = availableRecords.find((item) => item.key === key);

        if (record) {
          selectedRecord = record;
          refresh();
        }
      }
    });

    refresh();
  }

  async function showDesign(slug) {
    try {
      openModal();

      els.modalContent.innerHTML = `
        <div class="di-modal__loading">Loading design...</div>
      `;

      const design = await loadDesign(slug);

      if (!design) {
        els.modalContent.innerHTML = "<p>Design not found.</p>";
        return;
      }

      const availableRecords = getAvailableApparelRecords(design);
      const groups = groupApparelByCategory(availableRecords);
      const defaultRecord = getDefaultApparel(design);
      const artworkViews = getDesignArtworkViews(design);

      els.modalContent.innerHTML = `
        <div class="di-visualizer" data-visualizer-root>
          <div class="di-visualizer__preview" data-visualizer-preview>
            ${buildMockup(design, defaultRecord)}
          </div>

          <div class="di-visualizer__panel">
            <div class="di-visualizer__eyebrow">Design Details</div>

            <h2>${escapeHtml(titleWithCustomizableReference(design.title, design))}</h2>
            ${
              designIdeaDisplaySetting("showReferenceLine") &&
              customizableReference(design)
                ? `<div class="di-customizable-reference">Customizable Reference: ${escapeHtml(customizableReference(design))}</div>`
                : ""
            }

            <p>${escapeHtml(design.description || "")}</p>

            ${buildArtworkReferenceHtml(design, artworkViews)}

            <div class="di-visualizer__section">
              <div class="di-visualizer__label">Approved Apparel / SKU</div>
              <div class="di-visualizer__options">
                ${groups
                  .map((group) => {
                    return `
                      <button
                        type="button"
                        class="di-visualizer__option"
                        data-visualizer-category="${escapeHtml(group.key)}"
                        title="${escapeHtml(group.records[0]?.productName || group.label)}"
                      >
                        ${escapeHtml(group.label)}
                      </button>
                    `;
                  })
                  .join("")}
              </div>
            </div>

            <div class="di-visualizer__section">
              <div class="di-visualizer__label">
                Colors for <span data-visualizer-category-label></span>
              </div>
              <div class="di-visualizer__options" data-visualizer-color-options></div>
            </div>

            <div class="di-visualizer__section">
              <div class="di-visualizer__label">Selected Product Reference</div>
              <div class="di-visualizer__meta" data-visualizer-style-info></div>
            </div>

            ${
              isDesignCustomizable(design)
                ? `
                  <div class="di-visualizer__actions">
                    <a href="#" class="di-design-card__button" data-design-online-link>
                      Customize This Design
                    </a>
                  </div>
                `
                : ""
            }
          </div>
        </div>
      `;

      bindVisualizerControls(design);
    } catch (error) {
      console.error(error);
      els.modalContent.innerHTML = `<p>Unable to load design.</p>`;
    }
  }


  function injectCuratedVisualizerStyles() {
    /*
      v119:
      Always replace this runtime style block with the rules from the currently
      executing script. Older cached/duplicate Design Inspiration code could
      otherwise leave a stale desktop card min-height rule in the page because
      the old style element used the same id.
    */
    const existingStyle = document.getElementById("di-curated-visualizer-styles");
    if (existingStyle) existingStyle.remove();

    const style = document.createElement("style");
    style.id = "di-curated-visualizer-styles";
    style.dataset.diVersion = window.__UNISOURCE_DI_ACTIVE_VERSION__ || "v120-mobile-card-300-height";
    style.textContent = `
      /* v46: layout-only injected CSS. Artwork/apparel positioning comes from JSON. */

      .di-product-mockup-grid {
        width: 100%;
      }

      .di-product-mockup-grid[data-side-count="1"] { grid-template-columns: minmax(0, 1fr) !important; }
      .di-product-mockup-grid[data-side-count="2"] { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
      .di-product-mockup-grid[data-side-count="3"] { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
      .di-product-mockup-grid[data-side-count="4"] { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }

      /* Visualizer product reference link */
      .di-visualizer__product-link {
        display: flex !important;
        flex-direction: column !important;
        gap: 3px !important;
        padding: 8px 10px !important;
        margin: 3px -10px 0 !important;
        border: 1px solid transparent !important;
        border-radius: 8px !important;
        color: inherit !important;
        text-decoration: none !important;
        transition: background-color 160ms ease, border-color 160ms ease !important;
      }

      .di-visualizer__product-link:hover,
      .di-visualizer__product-link:focus-visible {
        background: #f4f7fb !important;
        border-color: #d7e1ef !important;
        text-decoration: none !important;
        outline: none !important;
      }

      .di-visualizer__product-link-name {
        color: #29496f !important;
        font-weight: 700 !important;
      }

      .di-visualizer__product-link-meta,
      .di-visualizer__product-link-description {
        color: #67778c !important;
      }

      .di-visualizer__product-link-action {
        margin-top: 3px !important;
        color: #194f8f !important;
        font-weight: 700 !important;
      }

      /* Keep the customizable-design CTA centered in every state */
      .di-visualizer__actions .di-design-card__button,
      [data-design-online-link] {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        min-height: 42px !important;
        padding: 11px 18px !important;
        text-align: center !important;
        line-height: 1.2 !important;
        white-space: normal !important;
      }

      .di-product-mockup {
        position: relative !important;
        display: block !important;
        width: 100% !important;
        min-height: 620px !important;
        overflow: hidden !important;
        border-radius: 16px !important;
        background: #f7f9fc !important;
      }

      /*
        v118 GRID CARD REFERENCE STAGE LOCK

        The storefront design card now keeps the original 348 x 372 grid
        reference plane for BOTH apparel and artwork. Admin Grid placement
        percentages were authored against this card-image coordinate space.
        On narrower cards/mobile, the complete reference plane is scaled as
        one unit instead of changing its aspect ratio.

        Result:
        - saved Grid apparel position remains unchanged
        - saved Grid artwork X/Y/scale/rotation remains unchanged
        - the original grid coordinate aspect ratio is preserved
        - garment and artwork shrink together
        - Modal/Admin rendering remains untouched
      */
      #DesignInspirationCenter .di-design-card .di-design-card__image-wrap {
        position: relative !important;
        width: 100% !important;
        min-height: 0 !important;
        height: auto !important;
        aspect-ratio: 348 / 372 !important;
        overflow: hidden !important;
      }

      #DesignInspirationCenter .di-design-card .di-product-mockup-grid {
        width: 100% !important;
        height: 100% !important;
        min-height: 0 !important;
        align-items: stretch !important;
      }

      #DesignInspirationCenter .di-design-card .di-product-mockup__view {
        position: relative !important;
        width: 100% !important;
        height: 100% !important;
        min-height: 0 !important;
        display: block !important;
        overflow: hidden !important;
      }

      #DesignInspirationCenter .di-design-card .di-product-mockup {
        position: relative !important;
        width: 100% !important;
        height: 100% !important;
        min-height: 0 !important;
        max-height: none !important;
        aspect-ratio: auto !important;
        overflow: hidden !important;
        border-radius: 12px !important;
        contain: layout paint !important;
        isolation: isolate !important;
      }

      #DesignInspirationCenter .di-design-card .di-product-mockup__grid-fixed-stage {
        position: absolute !important;
        left: 50% !important;
        top: 50% !important;
        width: 348px !important;
        height: 372px !important;
        transform: translate(-50%, -50%) scale(var(--di-grid-stage-scale, 1)) !important;
        transform-origin: center center !important;
        overflow: visible !important;
        pointer-events: none !important;
      }

      #DesignInspirationCenter .di-design-card .di-product-mockup__grid-fixed-stage > .di-product-mockup__shirt,
      #DesignInspirationCenter .di-design-card .di-product-mockup__grid-fixed-stage > .di-product-mockup__artwork,
      #DesignInspirationCenter .di-design-card .di-product-mockup__grid-fixed-stage > .di-product-mockup__placeholder {
        position: absolute !important;
        transform-origin: center center !important;
      }

      .di-product-mockup__shirt {
        z-index: 1 !important;
        display: block !important;
        object-fit: contain !important;
        opacity: 1 !important;
      }

      .di-product-mockup__artwork {
        position: absolute !important;
        z-index: 2 !important;
        height: auto !important;
        object-fit: contain !important;
        background: transparent !important;
        filter: drop-shadow(0 2px 3px rgba(15, 23, 42, .18)) !important;
        pointer-events: none !important;
      }


      .di-modal__dialog {
        width: min(97vw, 1420px) !important;
        max-width: 1420px !important;
      }

      .di-modal__content {
        align-items: start !important;
      }

      .di-modal .di-visualizer {
        grid-template-columns: minmax(0, 1.78fr) minmax(340px, 0.78fr) !important;
        gap: 32px !important;
      }

      .di-modal .di-visualizer__preview {
        min-width: 0 !important;
        width: 100% !important;
        min-height: auto !important;
        height: auto !important;
        padding: 14px 18px 10px !important;
        align-items: flex-start !important;
      }

      .di-modal .di-visualizer__preview .di-product-mockup-grid {
        margin: 0 auto !important;
        width: 100% !important;
      }

      .di-modal .di-visualizer__preview .di-product-mockup-grid[data-side-count="2"] {
        gap: 28px !important;
        align-items: start !important;
        margin-bottom: 0 !important;
      }

      /*
        v103 responsive locked mockup canvas

        Artwork and garment placement percentages are meaningful only when the
        coordinate canvas keeps the same proportions. Previously the modal
        mockup kept a fixed 500–540px height while its width changed, so opening
        DevTools or resizing the browser changed the canvas aspect ratio and
        made the artwork drift away from the garment.

        The mockup is now the shared responsive coordinate canvas for both the
        garment and artwork. Width controls height through aspect-ratio, so all
        saved X/Y/scale percentages resize together from the same origin.
      */
      .di-modal .di-visualizer__preview .di-product-mockup {
        width: 100% !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        aspect-ratio: 5 / 3 !important;
        contain: layout paint !important;
        isolation: isolate !important;
      }

      /* Two or more views use a taller per-side canvas while remaining locked. */
      .di-modal .di-visualizer__preview .di-product-mockup-grid[data-effective-side-count="2"] .di-product-mockup {
        aspect-ratio: 1 / 1 !important;
      }

      .di-modal .di-visualizer__preview .di-product-mockup-grid[data-effective-side-count="3"] .di-product-mockup,
      .di-modal .di-visualizer__preview .di-product-mockup-grid[data-effective-side-count="4"] .di-product-mockup {
        aspect-ratio: 4 / 5 !important;
      }

      .di-modal .di-product-mockup__shirt,
      .di-modal .di-product-mockup__artwork {
        transform-origin: center center !important;
        will-change: transform, left, top, width !important;
      }

      /*
        v105 fixed reference stage

        Saved apparel/artwork percentages are now resolved against a fixed
        1000 x 600 coordinate plane. The entire plane is uniformly scaled to
        fit the responsive mockup container, so opening DevTools or changing
        viewport width cannot change the relationship between garment and art.
      */
      .di-modal .di-product-mockup__stage {
        position: absolute !important;
        left: 50% !important;
        top: 50% !important;
        width: 1000px !important;
        height: 600px !important;
        transform-origin: center center !important;
        pointer-events: none !important;
        overflow: visible !important;
      }

      .di-modal .di-product-mockup__stage > .di-product-mockup__shirt,
      .di-modal .di-product-mockup__stage > .di-product-mockup__artwork,
      .di-modal .di-product-mockup__stage > .di-product-mockup__placeholder {
        position: absolute !important;
      }

      .di-product-mockup__caption,
      .di-visualizer__preview .di-product-mockup__caption,
      .di-modal .di-visualizer__preview .di-product-mockup__caption {
        margin-top: 4px !important;
      }

      .di-visualizer__option {
        line-height: 1.15 !important;
      }

      @media (max-width: 760px) {
        .di-modal__dialog {
          width: 100vw !important;
          max-width: 100vw !important;
        }

        .di-modal .di-visualizer {
          grid-template-columns: minmax(0, 1fr) !important;
          gap: 20px !important;
        }

        .di-product-mockup-grid,
        .di-product-mockup-grid[data-side-count] {
          grid-template-columns: minmax(0, 1fr) !important;
        }

        /*
          v120 MOBILE GRID CARD HEIGHT

          Override the desktop 372/378px card preview heights directly inside
          the active mobile breakpoint. This changes only storefront design-card
          preview height; the fixed 348 x 372 grid placement reference stage and
          modal 1000 x 600 reference stage remain unchanged.
        */
        #DesignInspirationCenter .di-design-card .di-design-card__image-wrap {
          height: 300px !important;
          min-height: 300px !important;
          max-height: 300px !important;
          aspect-ratio: auto !important;
        }

        #DesignInspirationCenter .di-design-card .di-product-mockup-grid,
        #DesignInspirationCenter .di-design-card .di-product-mockup-grid[data-side-count],
        #DesignInspirationCenter .di-design-card .di-product-mockup__view,
        #DesignInspirationCenter .di-design-card .di-product-mockup {
          height: 300px !important;
          min-height: 300px !important;
          max-height: 300px !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /*
    Keep the locked coordinate canvas stable after modal content, side count,
    apparel, or browser size changes. CSS performs the actual proportional
    scaling; ResizeObserver only records the live canvas dimensions and forces
    a repaint for browsers that retain stale absolutely-positioned layers.
  */
  const DI_GRID_FIXED_STAGE_WIDTH = 348;
  const DI_GRID_FIXED_STAGE_HEIGHT = 372;

  function fitGridFixedStage(mockup) {
    if (!mockup || mockup.dataset.viewMode !== "grid") return;

    const stage = mockup.querySelector(":scope > .di-product-mockup__grid-fixed-stage");
    if (!stage) return;

    const viewportWidth = Math.max(1, mockup.clientWidth || 1);
    const viewportHeight = Math.max(1, mockup.clientHeight || 1);
    const scale = Math.min(
      viewportWidth / DI_GRID_FIXED_STAGE_WIDTH,
      viewportHeight / DI_GRID_FIXED_STAGE_HEIGHT
    );

    mockup.style.setProperty("--di-grid-stage-scale", String(Math.max(0.0001, scale)));
    mockup.dataset.gridFixedStageWidth = String(DI_GRID_FIXED_STAGE_WIDTH);
    mockup.dataset.gridFixedStageHeight = String(DI_GRID_FIXED_STAGE_HEIGHT);
    mockup.dataset.gridFixedStageScale = String(scale);
  }

  function fitAllGridFixedStages() {
    document
      .querySelectorAll('#DesignInspirationCenter .di-design-card .di-product-mockup[data-view-mode="grid"]')
      .forEach(fitGridFixedStage);
  }

  const DI_REFERENCE_STAGE_WIDTH = 1000;
  const DI_REFERENCE_STAGE_HEIGHT = 600;

  function ensureFixedReferenceStage(mockup) {
    if (!mockup) return null;

    let stage = mockup.querySelector(":scope > .di-product-mockup__stage");

    if (!stage) {
      stage = document.createElement("div");
      stage.className = "di-product-mockup__stage";
      stage.setAttribute("aria-hidden", "true");

      const movable = Array.from(mockup.children).filter((child) =>
        child.classList &&
        (
          child.classList.contains("di-product-mockup__shirt") ||
          child.classList.contains("di-product-mockup__artwork") ||
          child.classList.contains("di-product-mockup__placeholder")
        )
      );

      movable.forEach((child) => stage.appendChild(child));
      mockup.insertBefore(stage, mockup.firstChild);
    }

    mockup.dataset.fixedReferenceStage = "true";
    return stage;
  }

  function fitFixedReferenceStage(mockup) {
    const stage = ensureFixedReferenceStage(mockup);
    if (!stage) return;

    const width = Math.max(1, mockup.clientWidth || 1);
    const height = Math.max(1, mockup.clientHeight || 1);
    const scale = Math.min(
      width / DI_REFERENCE_STAGE_WIDTH,
      height / DI_REFERENCE_STAGE_HEIGHT
    );

    stage.style.transform =
      `translate(-50%, -50%) scale(${Math.max(0.0001, scale)})`;

    mockup.dataset.referenceStageWidth = String(DI_REFERENCE_STAGE_WIDTH);
    mockup.dataset.referenceStageHeight = String(DI_REFERENCE_STAGE_HEIGHT);
    mockup.dataset.referenceStageScale = String(scale);
  }

  function fitAllFixedReferenceStages() {
    document
      .querySelectorAll('#di-modal .di-product-mockup[data-responsive-locked-canvas="true"]')
      .forEach(fitFixedReferenceStage);
  }


  function bindResponsiveMockupCanvasObserver() {
    if (window.__UNISOURCE_DI_MOCKUP_RESIZE_OBSERVER__) return;
    if (typeof ResizeObserver !== "function") return;

    function refreshEffectiveSideCount(grid) {
      if (!grid) return 1;

      const views = Array.from(
        grid.querySelectorAll(":scope > .di-product-mockup__view")
      );

      const visibleViews = views.filter((view) => {
        if (view.hidden) return false;
        if (view.style.display === "none") return false;

        const computed = window.getComputedStyle(view);
        if (computed.display === "none" || computed.visibility === "hidden") return false;

        const mockup = view.querySelector(".di-product-mockup");
        const shirt = mockup && mockup.querySelector(".di-product-mockup__shirt");
        if (!mockup || !shirt || shirt.style.display === "none") return false;

        return true;
      });

      const effectiveCount = Math.max(1, Math.min(4, visibleViews.length || 1));
      grid.dataset.effectiveSideCount = String(effectiveCount);
      return effectiveCount;
    }

    function refreshAllGrids() {
      document
        .querySelectorAll("#di-modal .di-product-mockup-grid")
        .forEach(refreshEffectiveSideCount);

      fitAllFixedReferenceStages();
      fitAllGridFixedStages();
    }

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const canvas = entry.target;
        const grid = canvas.closest(".di-product-mockup-grid");
        refreshEffectiveSideCount(grid);

        const width = Math.round(entry.contentRect.width || 0);
        const height = Math.round(entry.contentRect.height || 0);

        canvas.style.setProperty("--di-canvas-width", `${width}px`);
        canvas.style.setProperty("--di-canvas-height", `${height}px`);
        canvas.dataset.canvasWidth = String(width);
        canvas.dataset.canvasHeight = String(height);
        if (canvas.dataset.viewMode === "grid") {
          fitGridFixedStage(canvas);
        } else {
          fitFixedReferenceStage(canvas);
        }
      });
    });

    const observeCurrent = () => {
      refreshAllGrids();

      document
        .querySelectorAll(
          '#di-modal .di-product-mockup[data-responsive-locked-canvas="true"], ' +
          '#DesignInspirationCenter .di-design-card .di-product-mockup[data-view-mode="grid"]'
        )
        .forEach((canvas) => {
          if (canvas.dataset.resizeObserved === "true") return;
          canvas.dataset.resizeObserved = "true";

          if (canvas.dataset.viewMode === "grid") {
            fitGridFixedStage(canvas);
          } else {
            fitFixedReferenceStage(canvas);
          }

          observer.observe(canvas);

          canvas.querySelectorAll("img").forEach((image) => {
            if (image.dataset.diCanvasEventsBound === "true") return;
            image.dataset.diCanvasEventsBound = "true";
            image.addEventListener("load", refreshAllGrids);
            image.addEventListener("error", () => {
              window.requestAnimationFrame(refreshAllGrids);
            });
          });
        });
    };

    const mutationObserver = new MutationObserver(() => {
      window.requestAnimationFrame(observeCurrent);
    });

    mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "hidden", "class"],
    });

    window.addEventListener("resize", () => {
      window.requestAnimationFrame(observeCurrent);
    }, { passive: true });

    observeCurrent();

    window.__UNISOURCE_DI_MOCKUP_RESIZE_OBSERVER__ = observer;
    window.__UNISOURCE_DI_MOCKUP_MUTATION_OBSERVER__ = mutationObserver;
  }

  function recordLastClickTarget(event) {
    const element = event.target;
    window.__UNISOURCE_DI_LAST_CLICK_TARGET__ = {
      tag: element?.tagName || "",
      id: element?.id || "",
      className: String(element?.className || ""),
      text: String(element?.textContent || "").trim().slice(0, 80),
      isPagerButton: Boolean(element?.closest?.("[data-di-page-section][data-di-page]")),
      time: new Date().toISOString(),
    };
  }

  function handlePaginationClick(event) {
    recordLastClickTarget(event);
    const pageButton = event.target.closest?.("[data-di-page-section][data-di-page]");
    if (!pageButton) return false;

    const sectionName =
      pageButton.dataset.diPageSection || getSectionNameFromPagerElement(pageButton);
    const state = sectionState[sectionName];

    if (!state) return false;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const beforePage = state.page;
    const requestedPage = getRequestedPageFromPagerButton(pageButton, state);
    const changed = setSectionPage(sectionName, requestedPage, false);

    window.__UNISOURCE_DI_LAST_PAGER_EVENT__ = {
      version: window.__UNISOURCE_DI_ACTIVE_VERSION__,
      sectionName,
      action: pageButton.dataset.diPageAction || "number",
      buttonPage: pageButton.dataset.diPage,
      beforePage,
      requestedPage,
      afterPage: state.page,
      changed,
      itemCount: state.items.length,
      pageCount: getPageCount(state.items.length),
      time: new Date().toISOString(),
    };

    return true;
  }

  function bindEvents() {
    /*
      v61:
      Capture-phase pagination handler prevents stale bubbling handlers or older
      cached script instances from double-processing pager clicks.
    */
    if (!window.__UNISOURCE_DI_CAPTURE_PAGER_BOUND__) {
      document.addEventListener("click", handlePaginationClick, true);
      window.__UNISOURCE_DI_CAPTURE_PAGER_BOUND__ = true;
    }

    document.addEventListener("click", async (event) => {
      const pageButton = event.target.closest("[data-di-page-section][data-di-page]");

      if (pageButton) {
        /*
          Pagination is handled in capture phase by handlePaginationClick().
          Keep this guard only as a fallback if capture was blocked.
        */
        handlePaginationClick(event);
        return;
      }

      const button = event.target.closest("[data-open-design]");

      if (button) {
        event.preventDefault();
        const slug = button.dataset.openDesign;

        if (slug) {
          await showDesign(slug);
        }
      }
    });

    if (els.categoryPrev) {
      els.categoryPrev.addEventListener("click", () => scrollCategories(-1));
    }

    if (els.categoryNext) {
      els.categoryNext.addEventListener("click", () => scrollCategories(1));
    }

    if (els.primaryCategories) {
      els.primaryCategories.addEventListener("scroll", updateCategoryScrollButtons, { passive: true });
      window.addEventListener("resize", updateCategoryScrollButtons, { passive: true });

      els.primaryCategories.addEventListener("click", (event) => {
        const card = event.target.closest(".di-category-card");
        if (!card) return;

        const title =
          card.getAttribute("data-di-category-filter") ||
          card.querySelector(".di-category-card__title")?.textContent ||
          "";

        if (!title) return;

        event.preventDefault();
        setDesignCategoryFilter(title);
      });
    }

    if (els.featuredPrev) {
      els.featuredPrev.addEventListener("click", () => scrollFeatured(-1));
    }

    if (els.featuredNext) {
      els.featuredNext.addEventListener("click", () => scrollFeatured(1));
    }

    if (els.featuredScroller) {
      els.featuredScroller.addEventListener("scroll", updateFeaturedScrollerButtons, { passive: true });
      window.addEventListener("resize", updateFeaturedScrollerButtons, { passive: true });
    }

    if (els.modalClose) {
      els.modalClose.addEventListener("click", closeModal);
    }

    if (els.modal) {
      els.modal.addEventListener("click", (event) => {
        if (event.target === els.modal) {
          closeModal();
        }
      });
    }
  }

  window.UNISOURCE_DI_PAGINATION_LISTENERS_NOTE =
    "v69 uses capture-phase pagination for the All Design Ideas section.";

  window.UNISOURCE_DI_LAST_CLICK_TARGET = function () {
    return window.__UNISOURCE_DI_LAST_CLICK_TARGET__ || null;
  };

  window.UNISOURCE_DI_CARD_RENDER_ERRORS = function () {
    return window.__UNISOURCE_DI_CARD_RENDER_ERRORS__ || [];
  };

  window.UNISOURCE_DI_LAST_PAGE_RENDER = function () {
    return window.__UNISOURCE_DI_LAST_PAGE_RENDER__ || null;
  };

  window.UNISOURCE_DI_VERSION = function () {
    return {
      version: window.__UNISOURCE_DI_ACTIVE_VERSION__ || "unknown",
      capturePagerBound: Boolean(window.__UNISOURCE_DI_CAPTURE_PAGER_BOUND__),
      lastPagerEvent: window.__UNISOURCE_DI_LAST_PAGER_EVENT__ || null,
    };
  };

  window.UNISOURCE_DI_PAGINATION_INFO = function () {
    const info = Object.entries(sectionState).map(([section, state]) => ({
      section,
      page: state.page,
      itemCount: state.items.length,
      pageCount: getPageCount(state.items.length),
      pagerId: state.pager()?.id || "",
    }));

    console.table(info);
    return info;
  };

  window.UNISOURCE_DI_FORCE_PAGE = function (sectionName, pageNumber) {
    return setSectionPage(sectionName, Number(pageNumber), false);
  };

  window.UNISOURCE_DI_MOCKUP_DEBUG = function () {
    const rows = Array.from(document.querySelectorAll("#di-modal .di-product-mockup")).map((mockup) => {
      const shirt = mockup.querySelector(".di-product-mockup__shirt");
      const artwork = mockup.querySelector(".di-product-mockup__artwork");
      return {
        side: mockup.dataset.side || "",
        productId: mockup.dataset.productId || "",
        styleId: mockup.dataset.styleId || "",
        color: mockup.dataset.color || "",
        shirtSrc: shirt ? shirt.currentSrc || shirt.src || "" : "",
        artworkSrc: artwork ? artwork.currentSrc || artwork.src || "" : "",
        strictSource: mockup.dataset.strictMockupSource || "",
        fixedReferenceStage: mockup.dataset.fixedReferenceStage || "",
        referenceStageScale: mockup.dataset.referenceStageScale || "",
        canvasWidth: mockup.dataset.canvasWidth || "",
        canvasHeight: mockup.dataset.canvasHeight || "",
      };
    });
    console.table(rows);
    return rows;
  };

  window.UNISOURCE_DI_MATRIX_INFO = function () {
    const info = {
      matrixUrl: DI_MATRIX_URL,
      matrixLoaded: Boolean(DESIGN_MATRIX),
      matrixCounts: DESIGN_MATRIX?.counts || null,
      indexedDesignIds: DESIGN_MATRIX_BY_ID?.size || 0,
      indexedTitles: DESIGN_MATRIX_BY_TITLE?.size || 0,
    };

    console.table([info]);
    return info;
  };

  window.UNISOURCE_DI_DEBUG_MATRIX = function () {
    const cards = Array.from(document.querySelectorAll(".di-design-card")).map((card) => {
      const mockup = card.querySelector(".di-product-mockup");
      const artwork = card.querySelector(".di-product-mockup__artwork");
      const title = card.querySelector(".di-design-card__title")?.textContent?.trim() || "";

      return {
        title,
        matrixApplied: card.dataset.matrixApplied || mockup?.dataset.matrixApplied || "",
        productId: mockup?.dataset.productId || "",
        styleId: mockup?.dataset.styleId || "",
        matrixDefaultProductId: mockup?.dataset.matrixDefaultProductId || "",
        matrixDefaultStyleId: mockup?.dataset.matrixDefaultStyleId || "",
        placementPreset: artwork?.dataset.matrixPlacementPreset || "",
        placementStyle: artwork?.dataset.matrixPlacementStyle || "",
        apparelPositionStyle:
          mockup?.querySelector(".di-product-mockup__shirt")?.dataset
            .matrixApparelPositionStyle || "",
      };
    });

    console.table(cards);
    return cards;
  };

  function getAutoOpenDesignIdFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);

      const openMode = String(
        params.get("open") ||
          params.get("modal") ||
          params.get("view") ||
          ""
      ).toLowerCase();

      const shouldOpen =
        openMode === "visualizer" ||
        openMode === "visualize" ||
        openMode === "design" ||
        openMode === "true" ||
        params.has("designId") ||
        params.has("design_id") ||
        params.has("design") ||
        params.has("slug");

      if (!shouldOpen) return "";

      const requestedDesign = (
        params.get("designId") ||
        params.get("design_id") ||
        params.get("design") ||
        params.get("slug") ||
        ""
      ).trim();

      return normalizeDeepLinkDesignKey(requestedDesign) || requestedDesign;
    } catch (error) {
      return "";
    }
  }

  async function openDeepLinkedDesign() {
    const designId = getAutoOpenDesignIdFromUrl();
    if (!designId) return;

    try {
      await showDesign(designId);
    } catch (error) {
      console.error("[Design Inspiration] Unable to auto-open visualizer", {
        designId,
        error,
      });

      setStatus(
        "Design Inspiration Center loaded, but the selected visualizer could not be opened.",
        "error"
      );
    }
  }

  async function init() {
    try {
      injectCuratedVisualizerStyles();
      showLoader();
      setStatus("Loading Design Inspiration Center...");
      await loadDesignMatrix();

      const counts = await loadHome();

      setStatus(
        `Design Inspiration Center Ready — ${counts.total} designs loaded.`,
        "success"
      );

      await openDeepLinkedDesign();
    } catch (error) {
      console.error(error);
      setStatus("Unable to load Design Inspiration Center.", "error");
    } finally {
      hideLoader();
    }
  }

  bindEvents();

  // Start the responsive mockup observer before cards/modal are rendered.
  // v118 keeps grid cards on the original 348 x 372 reference coordinate stage
  // while preserving the existing modal reference-stage behavior.
  bindResponsiveMockupCanvasObserver();

  loadStorefrontDisplaySettings()
    .finally(() => init());
})();